import React from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { CalendarRange } from '../../Fields/Calendar';
import { CalendarDate, parseDate } from '@internationalized/date';
import {
  Text,
  ToastQueue,
  TooltipTrigger,
  Tooltip,
  ActionButton
} from '@adobe/react-spectrum';
import styles from './Features.module.scss';
import { mustBeValidUrl, isAllowedDemoUrl } from '../../Form/validators';
import RiskLowIcon from '../../../assets/icons/risk-level-low.svg';
import RiskModerateIcon from '../../../assets/icons/risk-level-moderate.svg';
import RiskHighIcon from '../../../assets/icons/risk-level-high.svg';
import RiskPlanningIcon from '../../../assets/icons/risk-level-planning.svg';
import CompletedCheckIcon from '../../../assets/icons/completed-circle-check.svg';
import PrdIcon from '../../../assets/icons/prd-icon-figma.svg';
import DemoIcon from '../../../assets/icons/demo-icon-figma.svg';
import ConfirmIconGray from '../../../assets/icons/confirm-icon-gray.svg';
import ConfirmIconBlue from '../../../assets/icons/confirm-icon-blue.svg';
import OptionsIcon from '../../../assets/icons/ellipsis-vertical.svg';
import AddIcon from '../../../assets/icons/add-icon.svg';
import DragHandleIcon from '../../../assets/icons/drag-handle.svg';
import DemoIconGray from '../../../assets/icons/demo-icon-gray.svg';
import PrdIconGray from '../../../assets/icons/prd-icon-gray.svg';
import EditIconGray from '../../../assets/icons/edit-icon-gray.svg';
import CheckIcon from '../../../assets/icons/dropdown-check.svg';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import FeatureHoverPopup, { FeatureHoverContent } from './FeatureHoverPopup';
import { useRBAC } from '../context/RBACContext';
import { useSelector } from 'react-redux';
import { selectAllSurfaces } from 'app/pages/Home/slice/selectors';
import {
  createFeature,
  updateFeature,
  deleteFeature,
  extractCreatedFeatureId,
  extractCreatedFeatureFromResponse
} from '../../../services/featuresApi';
import {
  RISK_LEVELS,
  STATUS_VALUES,
  riskLevelToApiLabel
} from '../../../../utils/constants';
import ToolTipWrapper from 'app/components/ToolTipWrapper/ToolTipWrapper';
import {
  FeatureSortSelection,
  sortFeaturesByOption
} from './FeaturesSortDropdown';
import { useReorderable } from '../../../hooks/useReorderable';
import type { AlertIssue } from '../../AlertsAndNotification/store/types';
import { useActiveAlertIssues } from '../../AlertsAndNotification/hooks/useActiveAlertIssues';
import {
  getFeatureAlertTarget,
  FeatureAlertCellSurface
} from '../../AlertsAndNotification/utils/featureDeclarativeHighlight';

export interface FeaturePrdData {
  url?: string;
  etaDate?: string;
  available?: boolean;
}

export interface FeatureRow {
  id: string;
  name: string;
  jiraLink?: string; // JIRA link for the feature
  riskLevel?: string;
  demo?: FeaturePrdData; // Demo data (same structure as PRD)
  prd?: FeaturePrdData; // PRD data
  internalBeta?: string; // Date or N/A
  internalBetaUserSet?: boolean; // Whether user explicitly set value
  privateBeta?: string; // Date or N/A
  privateBetaUserSet?: boolean; // Whether user explicitly set value
  publicBeta?: string; // Date or N/A
  publicBetaUserSet?: boolean; // Whether user explicitly set value
  ga?: string; // Date or N/A
  gaUserSet?: boolean; // Whether user explicitly set value
  surfaces?: string[]; // List of surfaces
  mvp?: boolean; // Checkbox state
  genAi?: boolean; // Checkbox state for GEN AI
  consumesCredit?: boolean; // Checkbox state for Consumes Credits
  status?: string; // ACTIVE | COMPLETED | ...
  confirmed?: boolean; // Whether the feature has been confirmed
}

export interface FeaturesProps {
  rows: FeatureRow[];
  onAddFeature?: () => void;
  groupKey?: string; // optional scoping key to isolate storage per product/group
  releaseId?: string; // Moment issueId under which features are created
  releaseName?: string; // Release/Moment display name
  productId?: string; // Product name/id for the current group
  /** Pass POST/PATCH/DELETE response body so slideout can apply moment/notifications envelope */
  onRefreshFeatures?: (patchResponse?: unknown) => void;
  defaultSurfaces?: string[]; // Default surfaces for new features (from moment API)
  highlightedFeatureId?: string | null; // Feature ID to highlight when coming from feature card click
  sortBy?: FeatureSortSelection; // Sort option; null = no sort (source order)
  /** Fired after a successful drag/drop or keyboard reorder with the new list order */
  onReorderCompleted?: (orderedFeatures: FeatureRow[]) => void;
  /**
   * Incremented by parent when server reorder fails so we rebuild API row order from `rows`
   * (same merge as rows fingerprint sync) without remounting.
   */
  serverListResyncKey?: number;
  readonly?: boolean; // Read-only mode: disables all editing/interactions
}

// Helper function to normalize URLs with proper protocol
const normalizeUrl = (url: string): string => {
  if (!url) return 'No url';

  // If URL already has a protocol, return as is (case-insensitive)
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  // For all other URLs (including www.example.com), add https://
  if (url.startsWith('www.') || url.includes('.')) {
    return `https://${url}`;
  }

  return url;
};

/** Local-only row from "Add Feature" before POST (`new-feature-…` ids). */
export const isUnsavedDraft = (id: string): boolean =>
  String(id).startsWith('new-feature-');

/**
 * Features table section (Figma-aligned)
 * - Presentational only for now; inline edits to be added later
 */
export const Features: React.FC<FeaturesProps> = ({
  rows,
  onAddFeature,
  groupKey,
  releaseId,
  releaseName,
  productId,
  onRefreshFeatures,
  defaultSurfaces,
  highlightedFeatureId,
  sortBy = null,
  onReorderCompleted,
  serverListResyncKey = 0,
  readonly = false
}) => {
  const location = useLocation();
  const { canEdit, isAdmin } = useRBAC();

  // Override edit permissions if readonly mode is enabled
  const hasEditAccess = readonly ? false : canEdit || isAdmin;
  const allSurfaces = useSelector(selectAllSurfaces);

  const defaultSurfacesRef = React.useRef<string[]>([]);

  // State to control highlight visibility (will auto-clear after delay)
  const [activeHighlightId, setActiveHighlightId] = React.useState<
    string | null
  >(null);

  // Alert-driven feature highlighting (date-past-due fieldRefs of shape
  // "feature.{productId}.{featureId}.{dateType}"). Declarative — computed per render from Redux
  // active alert issues — mirrors MilestoneMatrixFromAPI's getDeclarativeMilestoneHighlightIssue,
  // since Features rows (like milestone columns) are dynamically rendered from fetched data and a
  // DOM-query highlight pass would be timing-flaky. Unrelated to `activeHighlightId` above, which
  // drives the separate click-to-scroll pulse from FeatureCard clicks.
  const activeFeatureAlertIssues = useActiveAlertIssues(releaseId);

  // Grouped once per render (O(issues)) rather than re-scanning all active issues for every
  // row x surface lookup below (was O(3 * rows * issues) — noticeable with many features/alerts).
  const featureAlertHighlightMap = React.useMemo(() => {
    const map = new Map<
      string,
      Partial<Record<FeatureAlertCellSurface, AlertIssue>>
    >();
    for (const issue of activeFeatureAlertIssues) {
      const target = getFeatureAlertTarget(issue);
      if (!target) continue;
      const bySurface = map.get(target.featureId) ?? {};
      bySurface[target.surface] = issue;
      map.set(target.featureId, bySurface);
    }
    return map;
  }, [activeFeatureAlertIssues]);

  const getFeatureAlertHighlightIssue = React.useCallback(
    (featureId: string, surface: FeatureAlertCellSurface): AlertIssue | null =>
      featureAlertHighlightMap.get(featureId)?.[surface] ?? null,
    [featureAlertHighlightMap]
  );

  // ✅ FIX: Track which feature IDs have already been highlighted to prevent re-highlighting
  const highlightedFeaturesRef = React.useRef<Set<string>>(new Set());
  const prevReleaseIdRef = React.useRef<string | undefined>(releaseId);
  const highlightTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // ✅ Clear highlighted features set when navigating to a different release/moment
  React.useEffect(() => {
    if (releaseId !== prevReleaseIdRef.current) {
      // Different release/moment, clear the highlighted features set
      highlightedFeaturesRef.current.clear();
      prevReleaseIdRef.current = releaseId;
    }
  }, [releaseId]);

  // Auto-clear highlight after 3 seconds - only trigger once per featureId
  React.useEffect(() => {
    if (
      highlightedFeatureId &&
      !highlightedFeaturesRef.current.has(highlightedFeatureId)
    ) {
      // Mark this feature as highlighted so it won't highlight again
      highlightedFeaturesRef.current.add(highlightedFeatureId);

      // Clear any existing timer
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }

      setActiveHighlightId(highlightedFeatureId);

      // Set new timer and store reference
      highlightTimerRef.current = setTimeout(() => {
        setActiveHighlightId(null);
        highlightTimerRef.current = null;
      }, 3000);
    }

    // ✅ FIX: Don't return cleanup here - it gets called on every re-render
    // The timer should complete regardless of re-renders
  }, [highlightedFeatureId]);

  // ✅ Separate effect for cleanup on unmount only
  React.useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, []); // Empty deps = only runs on mount/unmount

  // Cleanup tooltip timers on unmount
  React.useEffect(() => {
    return () => {
      if (riskTooltipTimerRef.current) {
        clearTimeout(riskTooltipTimerRef.current);
        riskTooltipTimerRef.current = null;
      }
      if (nameTooltipTimerRef.current) {
        clearTimeout(nameTooltipTimerRef.current);
        nameTooltipTimerRef.current = null;
      }
    };
  }, []);

  // ✅ CENTRALIZED: Get moment metadata from context (single source of truth)
  // Validation message (global, bottom-right of card)
  const [validationMsgVisible, setValidationMsgVisible] = React.useState(false);
  // Track per-row missing required fields for new rows after failed submit
  const [missingRequired, setMissingRequired] = React.useState<{
    [featureId: string]: {
      name?: boolean;
      demo?: boolean;
      prd?: boolean;
      internalBeta?: boolean;
      privateBeta?: boolean;
      publicBeta?: boolean;
      ga?: boolean;
      surfaces?: boolean;
    };
  }>({});
  // Track which new rows have had validation triggered at least once
  const [validatedRows, setValidatedRows] = React.useState<Set<string>>(
    () => new Set()
  );

  // Toast helpers (centered Spectrum toasts)
  const showToast = (
    type: 'positive' | 'negative' | 'info' | 'notice',
    message: string
  ) => {
    const options = { timeout: 5000 } as any;
    if (!message) return;
    const sentenceCase = (str: string) => {
      const s = (str || '').trim();
      const idx = s.search(/[A-Za-z]/);
      if (idx === -1) return s;
      return s.slice(0, idx) + s.charAt(idx).toUpperCase() + s.slice(idx + 1);
    };
    const msg = sentenceCase(message);
    if (type === 'positive') return ToastQueue.positive(msg, options);
    if (type === 'negative') return ToastQueue.negative(msg, options);
    if (type === 'notice') return ToastQueue.neutral(msg, options);
    return ToastQueue.info(msg, options);
  };

  const isActivationKey = (ev: any) => {
    const key = (ev && ev.key) || '';
    return (
      key === 'Enter' ||
      key === ' ' ||
      key === 'Spacebar' ||
      key === 'NumpadEnter'
    );
  };

  // Always keep the latest non-empty default surfaces so Add Feature uses them even if prop was empty on first render
  React.useEffect(() => {
    if (Array.isArray(defaultSurfaces) && defaultSurfaces.length > 0) {
      defaultSurfacesRef.current = defaultSurfaces.filter(
        s => typeof s === 'string' && s.trim() !== ''
      );
    }
  }, [defaultSurfaces]);
  const storageKey = React.useMemo(
    () => `features:new-rows:${releaseId}${groupKey ? `:${groupKey}` : ''}`,
    [releaseId, groupKey]
  );

  const changesStorageKey = React.useMemo(
    () => `features:changes:${releaseId}${groupKey ? `:${groupKey}` : ''}`,
    [releaseId, groupKey]
  );

  // Detect hard refresh: Check on every mount, but use flag to differentiate
  // between hard reload and component remount within the same page session
  const isHardReload = React.useMemo(() => {
    // Check Performance API to see if this page load was a reload
    let wasPageReloaded = false;
    try {
      const navEntries = (performance &&
        (performance as any).getEntriesByType &&
        (performance.getEntriesByType('navigation') as any)) as
        | { type?: string }[]
        | undefined;
      if (navEntries && navEntries.length > 0) {
        wasPageReloaded =
          String(navEntries[0]?.type || '').toLowerCase() === 'reload';
      } else {
        const legacy = (performance as any).navigation;
        wasPageReloaded = legacy && legacy.type === 1;
      }
    } catch {
      wasPageReloaded = false;
    }

    // Use a session flag to track if we've already handled a reload in this page session
    // This prevents component remounts (collapse/expand) from being treated as reloads
    const sessionKey = 'features:hard-reload-handled';
    const alreadyHandled = sessionStorage.getItem(sessionKey);

    // If page was reloaded AND we haven't handled it yet in this page session
    if (wasPageReloaded && alreadyHandled !== 'true') {
      // Clear all features storage
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (
            key &&
            (key.startsWith('features:new-rows:') ||
              key.startsWith('features:changes:'))
          ) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
      } catch {
        // Ignore storage errors
      }

      // Mark that we've handled this reload
      sessionStorage.setItem(sessionKey, 'true');
      return true;
    }

    // If page was NOT reloaded (normal navigation), mark as handled so component
    // remounts within this page don't clear storage
    if (!wasPageReloaded && alreadyHandled !== 'true') {
      sessionStorage.setItem(sessionKey, 'true');
    }

    return false; // Not a hard reload (either normal nav or component remount)
  }, []);

  // Clear the reload flag when component unmounts due to page navigation
  // This ensures each new page load checks fresh
  React.useEffect(() => {
    const sessionKey = 'features:hard-reload-handled';

    // Clear flag on page unload so next reload is detected properly
    const handleBeforeUnload = () => {
      try {
        sessionStorage.removeItem(sessionKey);
      } catch {
        // Ignore
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const loadPersistedNewFeatures = React.useCallback((): FeatureRow[] => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as FeatureRow[]) : [];
    } catch {
      return [];
    }
  }, [storageKey]);

  const persistNewFeatures = React.useCallback(
    (features: FeatureRow[]) => {
      try {
        // ✅ Optimization: Only store if there are new features, otherwise remove the key
        if (features.length > 0) {
          sessionStorage.setItem(storageKey, JSON.stringify(features));
        } else {
          // Remove from storage when empty to avoid storing empty arrays
          sessionStorage.removeItem(storageKey);
        }
      } catch {
        // ignore storage failures
      }
    },
    [storageKey]
  );

  const loadPersistedChanges = React.useCallback((): Record<
    string,
    Partial<FeatureRow>
  > => {
    try {
      const raw = sessionStorage.getItem(changesStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [changesStorageKey]);

  const persistChanges = React.useCallback(
    (changes: Record<string, Partial<FeatureRow>>) => {
      try {
        // ✅ Optimization: Only store if there are changes, otherwise remove the key
        if (Object.keys(changes).length > 0) {
          sessionStorage.setItem(changesStorageKey, JSON.stringify(changes));
        } else {
          // Remove from storage when empty to avoid storing empty objects
          sessionStorage.removeItem(changesStorageKey);
        }
      } catch {
        // ignore storage failures
      }
    },
    [changesStorageKey]
  );

  // Track if we've initialized from storage (prevents reloading on every render)
  const hasInitializedFromStorage = React.useRef(false);
  /** Draft row ids removed after successful POST — excluded from smart-sync re-append. */
  const dismissedDraftIdsRef = React.useRef<Set<string>>(new Set());
  /** Real ids promoted from draft until the refetched `rows` prop includes them. */
  const promotedFeatureIdsRef = React.useRef<Set<string>>(new Set());

  // Local state for features list to handle adding new features
  const [localFeatures, setLocalFeatures] = React.useState<FeatureRow[]>(() => {
    // INITIAL STATE ONLY - this runs once on mount
    // On hard reload, do not restore any persisted new rows
    if (isHardReload) {
      hasInitializedFromStorage.current = true;
      return [...rows];
    }
    // Restore any locally persisted new rows that don't collide with API ids
    const persisted = loadPersistedNewFeatures();
    const existingIds = new Set(rows.map(r => r.id));
    const uniquePersisted = persisted.filter(p => !existingIds.has(p.id));

    // Load draft changes and apply to API rows
    const changes = loadPersistedChanges();
    const apiRowsWithChanges = rows.map(row => {
      const change = changes[row.id];
      return change ? { ...row, ...change } : row;
    });

    hasInitializedFromStorage.current = true;
    return [...apiRowsWithChanges, ...uniquePersisted];
  });

  // Track changes made to existing API rows
  const [featureChanges, setFeatureChanges] = React.useState<
    Record<string, Partial<FeatureRow>>
  >(() => {
    if (isHardReload) return {};
    return loadPersistedChanges();
  });
  const featureChangesRef = React.useRef(featureChanges);
  React.useLayoutEffect(() => {
    featureChangesRef.current = featureChanges;
  }, [featureChanges]);

  // Track confirmed features (those with blue tick)
  const [confirmedFeatures, setConfirmedFeatures] = React.useState<Set<string>>(
    () => {
      if (isHardReload) return new Set();
      const changes = loadPersistedChanges();
      return new Set(Object.keys(changes).filter(id => changes[id].confirmed));
    }
  );

  // Track features with invalid link attempts (to enable Save and show validation on confirm)
  const [invalidLinkAttempts, setInvalidLinkAttempts] = React.useState<
    Set<string>
  >(new Set());

  // Store the actual invalid link values so we can show them in the popup with error message
  const [invalidLinkValues, setInvalidLinkValues] = React.useState<
    Record<string, string>
  >({});

  // Track features with invalid Demo link attempts (to enable Save and show validation on confirm)
  const [invalidDemoLinkAttempts, setInvalidDemoLinkAttempts] = React.useState<
    Set<string>
  >(new Set());

  // Store the actual invalid Demo link values so we can show them in the popup with error message
  const [invalidDemoLinkValues, setInvalidDemoLinkValues] = React.useState<
    Record<string, string>
  >({});

  // Track features with invalid PRD link attempts (to enable Save and show validation on confirm)
  const [invalidPrdLinkAttempts, setInvalidPrdLinkAttempts] = React.useState<
    Set<string>
  >(new Set());

  // Store the actual invalid PRD link values so we can show them in the popup with error message
  const [invalidPrdLinkValues, setInvalidPrdLinkValues] = React.useState<
    Record<string, string>
  >({});

  // Local overrides for "original" feature snapshots after a successful save/delete
  // Used to avoid full refetch and to keep dirty tracking correct per-row
  const [originalOverrides, setOriginalOverrides] = React.useState<
    Record<string, FeatureRow>
  >({});

  // (debug logging removed)

  // Helper: does API feature have unsaved changes?
  const isApiFeatureDirty = React.useCallback(
    (featureId: string): boolean => {
      const changes = featureChanges[featureId];
      return !!changes && Object.keys(changes).length > 0;
    },
    [featureChanges]
  );

  // Helper: is feature complete (all required fields filled)?
  const isFeatureComplete = React.useCallback((row: FeatureRow): boolean => {
    const nameOk =
      !!row.name && row.name.trim() !== '' && row.name !== 'Feature Name';

    // For DEMO/PRD: consider complete if either a date (including N/A/TBD)
    // OR a valid URL is present
    const hasEtaOrUrl = (data?: FeaturePrdData) => {
      const hasAnyDate = hasEtaAny(data);
      const url = data?.url || '';
      const hasValidUrl = !!url && isValidUrl(String(url));
      return hasAnyDate || hasValidUrl;
    };

    const demoOk = hasEtaOrUrl(row.demo);
    const prdOk = hasEtaOrUrl(row.prd);

    // For beta/GA: empty string = placeholder (not valid), any other value = valid
    const dateOkFor = (
      key: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga',
      val?: string,
      userSet?: boolean
    ) => {
      const s = String(val || '').trim();
      // Empty string = placeholder, not valid
      // Any non-empty value (including 'N/A', 'TBD', dates, quarters, months) = valid
      return s !== '';
    };

    const internalOk = dateOkFor(
      'internalBeta',
      row.internalBeta,
      row.internalBetaUserSet
    );
    const privateOk = dateOkFor(
      'privateBeta',
      row.privateBeta,
      row.privateBetaUserSet
    );
    const publicOk = dateOkFor(
      'publicBeta',
      row.publicBeta,
      row.publicBetaUserSet
    );
    const gaOk = dateOkFor('ga', row.ga, row.gaUserSet);

    // Surfaces validation: must have at least one surface selected
    const surfacesOk = Array.isArray(row.surfaces) && row.surfaces.length > 0;

    return (
      nameOk &&
      demoOk &&
      prdOk &&
      internalOk &&
      privateOk &&
      publicOk &&
      gaOk &&
      surfacesOk
    );
  }, []);

  // Compute missing fields for display when validation fails on new row submit
  const getMissingRequired = React.useCallback((row: FeatureRow) => {
    const hasEtaOrUrl = (data?: FeaturePrdData) => {
      const hasAnyDate = hasEtaAny(data);
      const url = data?.url || '';
      const hasValidUrl = !!url && isValidUrl(String(url));
      return hasAnyDate || hasValidUrl;
    };
    const dateMissingFor = (
      key: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga',
      val?: string,
      userSet?: boolean
    ) => {
      const s = String(val || '').trim();
      // Empty string = missing/placeholder
      // Any non-empty value (including 'N/A', 'TBD', dates, etc.) = not missing
      return s === '';
    };

    return {
      name: !(
        !!row.name &&
        row.name.trim() !== '' &&
        row.name !== 'Feature Name'
      ),
      demo: !hasEtaOrUrl(row.demo),
      prd: !hasEtaOrUrl(row.prd),
      internalBeta: dateMissingFor(
        'internalBeta',
        row.internalBeta,
        row.internalBetaUserSet
      ),
      privateBeta: dateMissingFor(
        'privateBeta',
        row.privateBeta,
        row.privateBetaUserSet
      ),
      publicBeta: dateMissingFor(
        'publicBeta',
        row.publicBeta,
        row.publicBetaUserSet
      ),
      ga: dateMissingFor('ga', row.ga, row.gaUserSet),
      surfaces: !(Array.isArray(row.surfaces) && row.surfaces.length > 0)
    } as const;
  }, []);

  // Watch for changes and clear validation when rows become complete
  React.useEffect(() => {
    // Check each row that has validation errors
    const rowsWithErrors = Object.keys(missingRequired);
    if (rowsWithErrors.length === 0) return;

    let hasChanges = false;
    const updatedMissing = { ...missingRequired } as any;

    rowsWithErrors.forEach(featureId => {
      const feature = localFeatures.find(f => f.id === featureId);
      if (!feature) return;

      // If the entire row is now complete, drop its error entry
      if (isFeatureComplete(feature)) {
        delete updatedMissing[featureId];
        hasChanges = true;
      } else {
        // Otherwise, clear individual field validations as they become valid
        const currentErrors = updatedMissing[featureId];
        if (!currentErrors) return;

        const newErrors = { ...currentErrors };

        // Clear surfaces error if surfaces are now valid
        if (
          newErrors.surfaces &&
          Array.isArray(feature.surfaces) &&
          feature.surfaces.length > 0
        ) {
          delete newErrors.surfaces;
          hasChanges = true;
        }

        // Clear name error if name is now valid
        if (
          newErrors.name &&
          feature.name &&
          feature.name.trim() !== '' &&
          feature.name !== 'Feature Name'
        ) {
          delete newErrors.name;
          hasChanges = true;
        }

        // Clear demo error if demo is now valid
        if (newErrors.demo) {
          const hasAnyDate = hasEtaAny(feature.demo);
          const url = feature.demo?.url || '';
          const hasValidUrl = !!url && isValidUrl(String(url));
          if (hasAnyDate || hasValidUrl) {
            delete newErrors.demo;
            hasChanges = true;
          }
        }

        // Clear prd error if prd is now valid
        if (newErrors.prd) {
          const hasAnyDate = hasEtaAny(feature.prd);
          const url = feature.prd?.url || '';
          const hasValidUrl = !!url && isValidUrl(String(url));
          if (hasAnyDate || hasValidUrl) {
            delete newErrors.prd;
            hasChanges = true;
          }
        }

        // Clear date errors if dates are now valid
        if (
          newErrors.internalBeta &&
          String(feature.internalBeta || '').trim() !== ''
        ) {
          delete newErrors.internalBeta;
          hasChanges = true;
        }
        if (
          newErrors.privateBeta &&
          String(feature.privateBeta || '').trim() !== ''
        ) {
          delete newErrors.privateBeta;
          hasChanges = true;
        }
        if (
          newErrors.publicBeta &&
          String(feature.publicBeta || '').trim() !== ''
        ) {
          delete newErrors.publicBeta;
          hasChanges = true;
        }
        if (newErrors.ga && String(feature.ga || '').trim() !== '') {
          delete newErrors.ga;
          hasChanges = true;
        }

        // If no errors remain for this row, delete the entry
        if (Object.keys(newErrors).length === 0) {
          delete updatedMissing[featureId];
        } else {
          updatedMissing[featureId] = newErrors;
        }
      }
    });

    if (hasChanges) {
      setMissingRequired(updatedMissing);
      if (Object.keys(updatedMissing).length === 0) {
        setValidationMsgVisible(false);
      }
    }
  }, [localFeatures, isFeatureComplete]); // Remove missingRequired from deps to avoid circular dependency

  // Helper: decide confirm icon blue vs gray
  let isConfirmIconBlue = React.useCallback((row: FeatureRow): boolean => {
    // Placeholder; actual implementation is defined after getOriginalFeature
    return true;
  }, []);

  // Calendar popup state for beta dates
  const [calendarPopup, setCalendarPopup] = React.useState<{
    isVisible: boolean;
    featureId: string | null;
    fieldType: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga' | null;
    position: { x: number; y: number };
  }>({
    isVisible: false,
    featureId: null,
    fieldType: null,
    position: { x: 0, y: 0 }
  });

  // Refs for direct DOM manipulation of popups (for smooth scroll updates)
  const calendarPopupRef = React.useRef<HTMLDivElement>(null);
  const hoverPopupRef = React.useRef<HTMLDivElement>(null);
  const optionsMenuPopupRef = React.useRef<HTMLDivElement>(null);
  const riskDropdownPopupRef = React.useRef<HTMLDivElement>(null);
  const surfacesDropdownPopupRef = React.useRef<HTMLDivElement>(null);

  // Calendar opens on click only; no hover persistence state needed

  // When navigating to a different card (releaseId changes), reload from that card's storage
  React.useEffect(() => {
    // Skip initial mount (handled by useState)
    if (!hasInitializedFromStorage.current) {
      return;
    }

    // When releaseId changes, we're viewing a different card - reload from its storage
    // Load persisted draft changes for this card
    let changes: Record<string, Partial<FeatureRow>> = {};
    try {
      const raw = sessionStorage.getItem(changesStorageKey);
      changes = raw ? JSON.parse(raw) : {};
    } catch {
      changes = {};
    }

    // Apply persisted changes to API rows
    const apiRowsWithChanges = rows.map(row => {
      const change = changes[row.id];
      return change ? { ...row, ...change } : row;
    });

    // Load persisted new features for this card
    let persisted: FeatureRow[] = [];
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        persisted = Array.isArray(parsed) ? parsed : [];
      }
    } catch {
      persisted = [];
    }

    // Merge API rows with persisted new features (avoid duplicates)
    const existingIds = new Set(rows.map(r => r.id));
    const uniquePersisted = persisted.filter(
      p => !existingIds.has(p.id) && !dismissedDraftIdsRef.current.has(p.id)
    );
    setLocalFeatures([...apiRowsWithChanges, ...uniquePersisted]);

    // Update feature changes state for this card
    setFeatureChanges(changes);

    // Update confirmed features state
    setConfirmedFeatures(
      new Set(Object.keys(changes).filter(id => changes[id].confirmed))
    );
  }, [releaseId, storageKey, changesStorageKey]);

  React.useEffect(() => {
    dismissedDraftIdsRef.current.clear();
    promotedFeatureIdsRef.current.clear();
  }, [releaseId, groupKey]);

  const getActiveDraftRows = React.useCallback((features: FeatureRow[]) => {
    return features.filter(
      f =>
        f.id.startsWith('new-feature-') &&
        !dismissedDraftIdsRef.current.has(f.id)
    );
  }, []);

  const mergeRowsWithLocalState = React.useCallback(
    (
      prevLocalFeatures: FeatureRow[],
      updatedApiFeatures: FeatureRow[],
      activeDrafts: FeatureRow[]
    ): FeatureRow[] => {
      const rowIds = new Set(updatedApiFeatures.map(r => r.id));
      promotedFeatureIdsRef.current.forEach(id => {
        if (rowIds.has(id)) promotedFeatureIdsRef.current.delete(id);
      });
      const pendingPromoted = prevLocalFeatures.filter(
        f => promotedFeatureIdsRef.current.has(f.id) && !rowIds.has(f.id)
      );
      return [...updatedApiFeatures, ...pendingPromoted, ...activeDrafts];
    },
    []
  );

  // Parent rebuilds `rows` every render (new array ref), so [rows] would re-run the sync
  // effect on every parent update (e.g. sort) and wipe local reorder. Key off payload instead.
  const rowsPropFingerprint = React.useMemo(() => JSON.stringify(rows), [rows]);

  // Smart sync: When rows prop changes (API refetch after POST/PATCH/DELETE),
  // intelligently merge new API data while preserving unsaved features and draft changes
  React.useEffect(() => {
    // Skip if this is the initial mount (already initialized in useState)
    if (!hasInitializedFromStorage.current) {
      return;
    }

    // Update API features with latest data from rows prop
    // But keep any draft changes that haven't been saved yet
    setLocalFeatures(prevLocalFeatures => {
      const currentNewFeatures = getActiveDraftRows(prevLocalFeatures);

      // Update API features: use new data from rows, apply any draft changes
      // Note: featureChanges is read from closure and reflects current state
      const updatedApiFeatures = rows.map(row => {
        const hasChanges = featureChangesRef.current[row.id];
        if (hasChanges) {
          // Apply draft changes to the fresh API data
          return { ...row, ...hasChanges };
        }
        return row;
      });

      return mergeRowsWithLocalState(
        prevLocalFeatures,
        updatedApiFeatures,
        currentNewFeatures
      );
    });

    // Sync saved baselines: no pending changes → use fresh API rows via getOriginalFeature
    setOriginalOverrides(prev => {
      let changed = false;
      const next = { ...prev };
      rows.forEach(row => {
        if (!featureChangesRef.current[row.id] && next[row.id]) {
          delete next[row.id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rows keyed by rowsPropFingerprint; featureChanges read when API payload changes
  }, [rowsPropFingerprint, getActiveDraftRows, mergeRowsWithLocalState]);

  const prevServerListResyncKey = React.useRef(serverListResyncKey);
  React.useEffect(() => {
    if (!hasInitializedFromStorage.current) return;
    if (serverListResyncKey === prevServerListResyncKey.current) return;
    prevServerListResyncKey.current = serverListResyncKey;
    if (serverListResyncKey === 0) return;

    setLocalFeatures(prevLocalFeatures => {
      const currentNewFeatures = getActiveDraftRows(prevLocalFeatures);

      const updatedApiFeatures = rows.map(row => {
        const hasChanges = featureChangesRef.current[row.id];
        if (hasChanges) {
          return { ...row, ...hasChanges };
        }
        return row;
      });

      return mergeRowsWithLocalState(
        prevLocalFeatures,
        updatedApiFeatures,
        currentNewFeatures
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only react to resync key; rows + featureChanges from closure
  }, [serverListResyncKey, getActiveDraftRows, mergeRowsWithLocalState]);

  // Hover popup state - with persistence for editing
  const [hoverPopup, setHoverPopup] = React.useState<{
    isVisible: boolean;
    content: FeatureHoverContent | null;
    position: { x: number; y: number };
    arrowDirection: 'up' | 'down';
    anchorSelector?: string | null;
    openOffset?: { dx: number; dy: number } | null;
  }>({
    isVisible: false,
    content: null,
    position: { x: 0, y: 0 },
    arrowDirection: 'down',
    anchorSelector: null,
    openOffset: null
  });
  // Keep the exact element that opened the popup for stable repositioning
  const hoverAnchorElRef = React.useRef<HTMLElement | null>(null);

  const updateTooltipPosition = (content, rect, event) => {
    // Position relative to slideout/table root so it stays correct on small screens
    const overlayRoot = getOverlayRootElement() as HTMLElement;
    const rootRect = overlayRoot.getBoundingClientRect();

    // Remember the selector and offset so we can keep the exact same open position on scroll
    const fid = content?.featureId;
    const ft = content?.fieldType;
    let anchorSelector: string | null = null;
    if (fid) {
      if (ft === 'name')
        anchorSelector = `[data-testid="features-cell-name-text-${fid}"]`;
      else if (ft === 'demo')
        anchorSelector = `[data-testid="features-cell-demo-${fid}"]`;
      else if (ft === 'prd')
        anchorSelector = `[data-testid="features-cell-prd-${fid}"]`;
    }

    // Find the anchor element (cell container) for stable scroll repositioning
    let anchorElement: HTMLElement | null = null;
    if (anchorSelector) {
      anchorElement = document.querySelector(
        anchorSelector
      ) as HTMLElement | null;
    }

    // Use trigger element's position for initial placement (looks good visually)
    const triggerRect = rect;
    const relativeX = triggerRect.left - rootRect.left - 55;
    const relativeY = triggerRect.top - rootRect.top + 40;

    // Calculate offset between trigger and cell container for scroll repositioning
    let cellRect: DOMRect;
    if (anchorElement) {
      cellRect = anchorElement.getBoundingClientRect();
    } else {
      cellRect = triggerRect;
    }

    const baseLeft = cellRect.left - rootRect.left;
    const baseTop = cellRect.top - rootRect.top;
    const openOffset = { dx: relativeX - baseLeft, dy: relativeY - baseTop };

    // Store the anchor element (cell container) for stable repositioning on scroll
    hoverAnchorElRef.current =
      anchorElement || (event && (event.currentTarget as HTMLElement)) || null;

    setHoverPopup({
      isVisible: true,
      content,
      position: { x: relativeX, y: relativeY },
      arrowDirection: 'down',
      anchorSelector,
      openOffset
    });
  };

  // Track hover state for persistence
  const [isHoveringTrigger, setIsHoveringTrigger] = React.useState(false);
  const [isHoveringPopup, setIsHoveringPopup] = React.useState(false);
  // Risk level tooltip state and timer (like TimelineCard)
  const [openRiskTooltip, setOpenRiskTooltip] = React.useState<string | null>(
    null
  );
  const riskTooltipTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  // Feature name tooltip state and timer (like TimelineCard)
  const [openNameTooltip, setOpenNameTooltip] = React.useState<string | null>(
    null
  );
  const nameTooltipTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  // Track hover for DEMO and PRD cells to show edit icon
  const [hoveredDemoRowId, setHoveredDemoRowId] = React.useState<string | null>(
    null
  );
  const [hoveredPrdRowId, setHoveredPrdRowId] = React.useState<string | null>(
    null
  );
  const [focusedDemoEditRowId, setFocusedDemoEditRowId] = React.useState<
    string | null
  >(null);
  const [focusedPrdEditRowId, setFocusedPrdEditRowId] = React.useState<
    string | null
  >(null);
  // Refs to edit buttons so keyboard Tab can move focus reliably
  const nameEditButtonRefs = React.useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});
  const demoEditButtonRefs = React.useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});
  const prdEditButtonRefs = React.useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});
  const demoPlaceholderRefs = React.useRef<{
    [key: string]: HTMLElement | null;
  }>({});
  const prdPlaceholderRefs = React.useRef<{
    [key: string]: HTMLElement | null;
  }>({});
  const internalBetaBtnRefs = React.useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});

  const focusDeferred = (el: HTMLElement | null) => {
    if (!el) return;
    // Defer to allow React state (hover) to render the button first
    setTimeout(() => el.focus(), 0);
  };

  // Options dropdown state (kebab menu)
  const [optionsMenu, setOptionsMenu] = React.useState<{
    featureId: string | null;
    position: { x: number; y: number };
  }>({ featureId: null, position: { x: 0, y: 0 } });
  const optionsMenuRef = React.useRef<HTMLDivElement | null>(null);
  const optionsAnchorRef = React.useRef<{
    left: number;
    right: number;
    boundaryWidth: number;
  } | null>(null);
  const optionsMenuWidthRef = React.useRef<number>(200);

  /** After clone: scroll row into view and open name editor (cleared in layout effect). */
  const [pendingCloneFocus, setPendingCloneFocus] = React.useState<{
    id: string;
    name: string;
    jiraLink?: string;
  } | null>(null);

  // Risk level dropdown state (portal-based)
  const [riskLevelDropdownOpen, setRiskLevelDropdownOpen] = React.useState<{
    featureId: string | null;
    position: { x: number; y: number };
  }>({ featureId: null, position: { x: 0, y: 0 } });
  const riskButtonRefs = React.useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});

  // Surfaces dropdown state (click-activated)
  const [surfacesDropdownOpen, setSurfacesDropdownOpen] = React.useState<
    string | null
  >(null);
  const surfacesCellRefs = React.useRef<{
    [key: string]: HTMLElement | null;
  }>({});
  // Persist options per row so user can re-select deselected items
  const surfacesOptionsRef = React.useRef<{ [key: string]: string[] }>({});

  // Scroll functionality state
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [showLeftChevron, setShowLeftChevron] = React.useState(false);
  const [showRightChevron, setShowRightChevron] = React.useState(false);
  const rafRef = React.useRef<number | null>(null);

  // Auto-focus first item in surfaces dropdown when opened
  React.useEffect(() => {
    if (surfacesDropdownOpen) {
      setTimeout(() => {
        const firstItem = document.querySelector(
          `[data-testid="features-surfaces-item-all"]`
        ) as HTMLElement;
        if (firstItem) firstItem.focus();
      }, 0);
    }
  }, [surfacesDropdownOpen]);

  // Auto-focus first item in risk level dropdown when opened
  React.useEffect(() => {
    if (riskLevelDropdownOpen.featureId) {
      setTimeout(() => {
        const firstItem = document.querySelector(
          `[data-testid="features-risk-item-low"]`
        ) as HTMLElement;
        if (firstItem) firstItem.focus();
      }, 0);
    }
  }, [riskLevelDropdownOpen.featureId]);

  // Auto-focus first item in options menu when opened
  React.useEffect(() => {
    if (optionsMenu.featureId) {
      setTimeout(() => {
        const firstItem = document.querySelector(
          `[data-testid="features-options-complete"]`
        ) as HTMLElement;
        const deleteItem = document.querySelector(
          `[data-testid="features-options-delete"]`
        ) as HTMLElement;
        // Focus first enabled item
        if (firstItem && firstItem.getAttribute('aria-disabled') !== 'true') {
          firstItem.focus();
        } else if (deleteItem) {
          deleteItem.focus();
        }
      }, 0);
    }
  }, [optionsMenu.featureId]);

  // Hide popup only when not hovering either trigger or popup
  React.useEffect(() => {
    if (!isHoveringTrigger && !isHoveringPopup) {
      const timer = setTimeout(() => {
        setHoverPopup(prev => ({ ...prev, isVisible: false }));
      }, 100); // Small delay to prevent flicker
      return () => clearTimeout(timer);
    }
  }, [isHoveringTrigger, isHoveringPopup]);

  // Click outside handler to close risk level dropdown
  React.useEffect(() => {
    if (!riskLevelDropdownOpen.featureId) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const riskButton =
        riskButtonRefs.current[riskLevelDropdownOpen.featureId!];
      const dropdownElement = document.querySelector(
        `[data-feature-risk-dropdown="${riskLevelDropdownOpen.featureId}"]`
      );

      // Check if click is inside the dropdown menu or on the risk button
      const isInsideDropdown = dropdownElement?.contains(target);
      const isOnRiskButton = riskButton?.contains(target);

      // Close dropdown if clicking outside both the button and dropdown
      if (!isInsideDropdown && !isOnRiskButton) {
        setRiskLevelDropdownOpen({ featureId: null, position: { x: 0, y: 0 } });
      }
    };

    // Use capture phase to ensure we catch the event early
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [riskLevelDropdownOpen.featureId]);

  // Close surfaces dropdown on outside click or Escape
  React.useEffect(() => {
    if (!surfacesDropdownOpen) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement;
      const dropdownEl = document.querySelector(
        `[data-feature-surfaces-dropdown="${surfacesDropdownOpen}"]`
      );
      const triggerEl = surfacesCellRefs.current[surfacesDropdownOpen];
      if (dropdownEl && dropdownEl.contains(target)) return;
      if (triggerEl && triggerEl.contains(target)) return;
      setSurfacesDropdownOpen(null);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setSurfacesDropdownOpen(null);
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [surfacesDropdownOpen]);

  // Surfaces dropdown: now click-based, no hover hide

  const getRiskIcon = (risk?: string, isCompletedRow?: boolean) => {
    if (isCompletedRow) return CompletedCheckIcon;
    const v = (risk || '').toLowerCase();
    if (v.includes('high') || v === 'red') return RiskHighIcon;
    if (v.includes('moderate') || v === 'yellow') return RiskModerateIcon;
    if (v.includes('planning') || v === 'in_planning') return RiskPlanningIcon;
    return RiskLowIcon;
  };

  const getRiskTooltipMessage = (
    risk?: string,
    isCompletedRow?: boolean
  ): React.ReactNode => {
    if (isCompletedRow) return 'Feature Completed';
    const v = (risk || '').toLowerCase();
    let riskText = 'Low';
    if (v.includes('high') || v === 'red') riskText = 'High';
    else if (v.includes('moderate') || v === 'yellow') riskText = 'Moderate';
    else if (v.includes('planning') || v === 'in_planning')
      riskText = 'In Planning';

    // For "In Planning", show without "Risk Level:" prefix and without bold
    if (v.includes('planning') || v === 'in_planning') {
      return <>{riskText}</>;
    }

    return (
      <>
        Risk Level: <span style={{ fontWeight: 'bold' }}>{riskText}</span>
      </>
    );
  };

  // Abbreviation map for surfaces — per Figma spec (M=Mobile, W=Web, DT=Desktop)
  const surfaceAbbreviations: Record<string, string> = {
    mobile: 'M',
    web: 'W',
    desktop: 'DT'
  };

  const abbreviateSurface = (surface: string): string => {
    const lower = surface.toLowerCase().trim();
    return surfaceAbbreviations[lower] || surface;
  };

  const formatSurfaces = (surfaces?: string[]): string => {
    if (Array.isArray(surfaces) && surfaces.length > 0) {
      // Show abbreviated list: "M, W, DT" instead of "3 Selected"
      return surfaces.map(abbreviateSurface).join(', ');
    }
    if (Array.isArray(surfaces) && surfaces.length === 0) {
      return 'Select';
    }
    // Undefined or missing -> N/A
    return 'N/A';
  };

  // Build payload helpers for POST (new feature)
  type PhasePayload = {
    type: string;
    date: string;
    quarters: string[];
    months: string[];
    value: string;
  };

  const buildPhaseDatePayload = (raw?: string): PhasePayload => {
    const empty: PhasePayload = {
      type: '',
      date: '',
      quarters: [],
      months: [],
      value: ''
    };
    if (!raw) return empty;
    const s = String(raw).trim();
    const upper = s.toUpperCase();
    if (upper === 'N/A' || upper === 'TBD') {
      return { ...empty, type: 'special', value: upper };
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return { ...empty, type: 'date', date: s };
    }
    if (/^Q[1-4]\s\d{4}$/i.test(s)) {
      return { ...empty, type: 'quarters', quarters: [s] };
    }
    if (/^[A-Za-z]{3}(?:\s+\d{4})?$/.test(s)) {
      // Month formats like "Sep" or "Sep 2025"
      return { ...empty, type: 'months', months: [s] };
    }
    // Unknown format – leave empty to avoid sending incorrect data
    return empty;
  };

  const mapApiLinkDateToFeaturePrdData = (
    apiField: unknown
  ): FeaturePrdData => {
    if (!apiField || typeof apiField !== 'object') {
      return { url: '', etaDate: '', available: false };
    }
    const field = apiField as { link?: string; date?: string };
    return {
      url: field.link ?? '',
      etaDate: field.date ?? '',
      available: undefined
    };
  };

  const applyPatchFeatureToRow = (
    row: FeatureRow,
    patchFeature: Record<string, unknown>
  ): FeatureRow => {
    const next = { ...row };
    if ('demo' in patchFeature) {
      next.demo = mapApiLinkDateToFeaturePrdData(patchFeature.demo);
    }
    if ('prd' in patchFeature) {
      next.prd = mapApiLinkDateToFeaturePrdData(patchFeature.prd);
    }
    if ('nameLink' in patchFeature) {
      next.jiraLink = String(patchFeature.nameLink ?? '');
    }
    if (typeof patchFeature.name === 'string') {
      next.name = patchFeature.name;
    }
    return next;
  };

  const buildCreateFeaturePayload = (
    feature: FeatureRow,
    context?: { releaseId?: string; productId?: string }
  ) => {
    return {
      releaseId: context?.releaseId || '',
      productId: context?.productId || '',
      name: feature.name || '',
      nameLink: feature.jiraLink || '',
      riskLevel: riskLevelToApiLabel(feature.riskLevel),
      status: STATUS_VALUES.ACTIVE,
      mvp: !!feature.mvp,
      genai: !!feature.genAi,
      consumesCredit: !!feature.consumesCredit,
      demo: {
        link: feature.demo?.url || '',
        date: feature.demo?.etaDate || ''
      },
      ppc: '',
      prd: {
        link: feature.prd?.url || '',
        date: feature.prd?.etaDate || ''
      },
      internalBeta: buildPhaseDatePayload(feature.internalBeta),
      privateBeta: buildPhaseDatePayload(feature.privateBeta),
      publicBeta: buildPhaseDatePayload(feature.publicBeta),
      ga: buildPhaseDatePayload(feature.ga),
      // Include selected surfaces (array of strings). Empty array if none.
      surfaces: Array.isArray(feature.surfaces) ? feature.surfaces : []
    };
  };

  // Display helper for eta values that might be stored as strings or legacy objects
  const formatEtaForDisplay = (val: any): string => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      if (val.type === 'special' && val.value) return String(val.value);
      if (val.type === 'date' && val.date) return String(val.date);
      if (Array.isArray(val.months) && val.months[0])
        return String(val.months[0]);
      if (Array.isArray(val.quarters) && val.quarters[0])
        return String(val.quarters[0]);
    }
    return '';
  };

  // Only show ETA text in cells if it is a real date/month/quarter, not special placeholders
  const isEtaDisplayable = (val: any): boolean => {
    const s = formatEtaForDisplay(val).trim();
    return s !== '' && s.toUpperCase() !== 'N/A' && s.toUpperCase() !== 'TBD';
  };

  // Consider any non-empty formatted ETA (including 'N/A'/'TBD') as set for PRD/DEMO display
  const hasEtaAny = (data?: FeaturePrdData): boolean => {
    const s = formatEtaForDisplay(data && (data as any).etaDate).trim();
    return s !== '';
  };

  const normalizeEtaForDateComponent = React.useCallback(
    (raw: string | undefined) => {
      if (!raw || raw === '') return '' as any;
      const v = String(raw).trim();
      if (v === 'N/A' || v === 'TBD')
        return { type: 'special', value: v } as any;
      if (/^Q[1-4]\s\d{4}$/i.test(v))
        return { type: 'quarters', quarters: [v] } as any;
      if (/^([A-Za-z]{3})(?:\s+\d{4})?$/i.test(v)) {
        return { type: 'months', months: [v] } as any;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(v))
        return { type: 'date', date: v } as any;
      return '' as any;
    },
    []
  );

  const liveHoverPopupContent =
    React.useMemo((): FeatureHoverContent | null => {
      const base = hoverPopup.content;
      if (!base?.featureId) return base;

      const fieldType = base.fieldType;
      if (fieldType !== 'demo' && fieldType !== 'prd') return base;

      const currentFeature = localFeatures.find(f => f.id === base.featureId);
      const actualFieldData = currentFeature?.[fieldType];
      const displayUrl =
        fieldType === 'demo' && invalidDemoLinkValues[base.featureId]
          ? invalidDemoLinkValues[base.featureId]
          : fieldType === 'prd' && invalidPrdLinkValues[base.featureId]
          ? invalidPrdLinkValues[base.featureId]
          : actualFieldData?.url || '';

      return {
        ...base,
        etaDate: normalizeEtaForDateComponent(actualFieldData?.etaDate),
        url: displayUrl
      };
    }, [
      hoverPopup.content,
      localFeatures,
      invalidDemoLinkValues,
      invalidPrdLinkValues,
      normalizeEtaForDateComponent
    ]);

  // Helpers to compare to original API data for accurate dirty tracking
  const getOriginalFeature = React.useCallback(
    (featureId: string) =>
      originalOverrides[featureId] || rows.find(r => r.id === featureId),
    [rows, originalOverrides]
  );

  const arraysEqualUnordered = (a?: string[], b?: string[]) => {
    const aArr = Array.isArray(a) ? [...a] : [];
    const bArr = Array.isArray(b) ? [...b] : [];
    if (aArr.length !== bArr.length) return false;
    aArr.sort();
    bArr.sort();
    return aArr.every((v, i) => v === bArr[i]);
  };

  // Now that getOriginalFeature is declared, finalize isConfirmIconBlue
  isConfirmIconBlue = React.useCallback(
    (row: FeatureRow): boolean => {
      if (row.id.startsWith('new-feature-')) {
        return true;
      }

      // Enable Save button if there's an invalid link attempt (JIRA link or Demo link)
      if (invalidLinkAttempts.has(row.id)) return true;
      if (invalidDemoLinkAttempts.has(row.id)) return true;
      if (invalidPrdLinkAttempts.has(row.id)) return true;

      return isApiFeatureDirty(row.id);
    },
    [
      isApiFeatureDirty,
      invalidLinkAttempts,
      invalidDemoLinkAttempts,
      invalidPrdLinkAttempts
    ]
  );

  // Safe phase label for table cells - avoids rendering raw objects
  const safePhaseLabel = (val: any): string => {
    const s = formatEtaForDisplay(val).trim();
    return s !== '' ? s : 'N/A';
  };

  // URL validation helper
  const isValidUrl = (string: string): boolean => !mustBeValidUrl(string);
  const isJiraUrl = (value: string): boolean =>
    /^https?:\/\/jira\.corp\.adobe\.com\/browse\/[A-Za-z]+-[0-9]+\/?$/i.test(
      (value || '').trim()
    );

  // Demo link validation - uses shared isAllowedDemoUrl from validators
  const isValidDemoUrl = (value: string): boolean => {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;
    if (!isValidUrl(trimmed)) return false;
    return isAllowedDemoUrl(trimmed);
  };

  // PRD link validation - standard URL format only (any valid http/https URL)
  const isValidPrdUrl = (value: string): boolean => {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;
    return isValidUrl(trimmed);
  };

  // Hover popup handlers - with persistence for editing
  const handleMouseEnter = (
    event: React.MouseEvent | React.KeyboardEvent,
    fieldType: 'prd' | 'demo',
    fieldData: any,
    featureId: string
  ) => {
    setIsHoveringTrigger(true);
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const popupWidth = 280;
    const popupHeight = 120; // Estimated popup height
    const margin = 16; // Minimum margin from panel edges

    // Get the actual current data from localFeatures to ensure we have the latest
    const currentFeature = localFeatures.find(f => f.id === featureId);
    const actualFieldData = currentFeature
      ? currentFeature[fieldType]
      : fieldData;

    // Use the stored invalid link value if it exists (for showing error), otherwise use actual URL
    const displayUrl =
      fieldType === 'demo' && invalidDemoLinkValues[featureId]
        ? invalidDemoLinkValues[featureId]
        : fieldType === 'prd' && invalidPrdLinkValues[featureId]
        ? invalidPrdLinkValues[featureId]
        : actualFieldData?.url || '';

    const content: FeatureHoverContent = {
      fieldType,
      featureId, // Add featureId to help with tracking
      etaDate: normalizeEtaForDateComponent(actualFieldData?.etaDate),
      url: displayUrl,
      disableTBD: true,
      disableNA: fieldType !== 'demo', // Enable N/A only for demo field
      onSave: data => {
        // Persist PRD/DEMO changes
        handlePrdDemoSave(
          featureId,
          fieldType,
          data.etaDate || '',
          data.url || ''
        );
      }
    };

    updateTooltipPosition(content, rect, event);
  };

  const handlePopupMouseEnter = () => {
    setIsHoveringPopup(true);
  };

  const handlePopupMouseLeave = () => {
    setIsHoveringPopup(false);
  };

  // Risk level tooltip handlers (like TimelineCard)
  const handleRiskTooltipMouseEnter = (featureId: string) => {
    if (riskTooltipTimerRef.current) {
      clearTimeout(riskTooltipTimerRef.current);
    }
    riskTooltipTimerRef.current = setTimeout(() => {
      setOpenRiskTooltip(featureId);
    }, 1000);
  };

  const handleRiskTooltipMouseLeave = () => {
    if (riskTooltipTimerRef.current) {
      clearTimeout(riskTooltipTimerRef.current);
    }
    setOpenRiskTooltip(null);
  };

  // Feature name tooltip handlers (like TimelineCard)
  const handleNameTooltipMouseEnter = (featureId: string) => {
    if (nameTooltipTimerRef.current) {
      clearTimeout(nameTooltipTimerRef.current);
    }
    nameTooltipTimerRef.current = setTimeout(() => {
      setOpenNameTooltip(featureId);
    }, 1000);
  };

  const handleNameTooltipMouseLeave = () => {
    if (nameTooltipTimerRef.current) {
      clearTimeout(nameTooltipTimerRef.current);
    }
    setOpenNameTooltip(null);
  };

  // Name field click handler (opens popup for editing name/JIRA for new features only)
  const handleNameClick = (
    event: React.MouseEvent | React.KeyboardEvent,
    featureId: string,
    featureName: string,
    jiraLink?: string
  ) => {
    // Keep popup open (disable auto-hide timer tied to hover state)
    setIsHoveringTrigger(true);
    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 420; // Increased width for more comfortable inputs
    const popupHeight = 160; // Height for two fields with proper spacing
    const margin = 16;

    // Create content for name editing
    // For new features with placeholder text, show empty value in popup
    const displayName = featureName === 'Feature Name' ? '' : featureName;
    const placeholderText =
      featureName === 'Feature Name' ? 'Feature Name' : 'Enter feature name';

    // Use the stored invalid link value if it exists (for showing error), otherwise use actual jiraLink
    const displayJiraLink = invalidLinkValues[featureId] || jiraLink || '';

    const content: FeatureHoverContent = {
      fieldType: 'name' as any, // We'll extend the type
      featureId,
      featureName: displayName,
      jiraLink: displayJiraLink,
      placeholderText,
      onSave: data => {
        handleNameSave(featureId, data.featureName || '', data.jiraLink || '');
      }
    };

    updateTooltipPosition(content, rect, event);
  };

  const handleNameClickRef = React.useRef(handleNameClick);
  handleNameClickRef.current = handleNameClick;

  // Handle name and jira link save
  const handleNameSave = (
    featureId: string,
    newName: string,
    jiraLink: string
  ) => {
    // If name is empty, set it back to placeholder for new features
    const trimmedName = newName.trim();
    const finalName = trimmedName || 'Feature Name';
    const trimmedJiraLink = jiraLink.trim();

    // Validate JIRA link if provided - if invalid, keep the original link
    let finalJiraLink = trimmedJiraLink;
    let hasInvalidLinkAttempt = false;
    if (
      trimmedJiraLink &&
      !(isValidUrl(trimmedJiraLink) || isJiraUrl(trimmedJiraLink))
    ) {
      // Invalid URL - keep the original link from the feature
      const currentFeature = localFeatures.find(f => f.id === featureId);
      finalJiraLink = currentFeature?.jiraLink || '';
      hasInvalidLinkAttempt = true;
    }

    setLocalFeatures(prev => {
      const next = prev.map(feature =>
        feature.id === featureId
          ? { ...feature, name: finalName, jiraLink: finalJiraLink }
          : feature
      );

      // Also update changes for API rows
      if (!featureId.startsWith('new-feature-')) {
        setFeatureChanges(prevChanges => {
          const original = getOriginalFeature(featureId);
          // For existing features, setting name to "Feature Name" should be treated as a change
          const isNameChanged = (original?.name || '') !== finalName;
          const isJiraChanged = (original?.jiraLink || '') !== finalJiraLink;

          // Start from current changes for this feature
          const nextForRow: any = { ...(prevChanges[featureId] || {}) };

          if (isNameChanged) nextForRow.name = finalName;
          else delete nextForRow.name;

          if (isJiraChanged) nextForRow.jiraLink = trimmedJiraLink;
          else delete nextForRow.jiraLink;

          // Remove entry entirely if no changes remain
          const hasKeys = Object.keys(nextForRow).length > 0;
          const nextAll = { ...prevChanges } as any;
          if (hasKeys) nextAll[featureId] = nextForRow;
          else delete nextAll[featureId];

          persistChanges(nextAll);
          return nextAll;
        });
      }

      // Persist new features to localStorage
      const newFeatures = next.filter(f => f.id.startsWith('new-feature-'));
      persistNewFeatures(newFeatures);

      return next;
    });

    // Track invalid link attempt for this feature (enables Save button)
    if (hasInvalidLinkAttempt) {
      setInvalidLinkAttempts(prev => {
        const next = new Set(prev);
        next.add(featureId);
        return next;
      });
      // Store the invalid link value so we can show it in the popup with error
      setInvalidLinkValues(prev => ({
        ...prev,
        [featureId]: trimmedJiraLink
      }));
    } else {
      // Clear invalid link attempt if link is now valid or empty
      setInvalidLinkAttempts(prev => {
        if (!prev.has(featureId)) return prev;
        const next = new Set(prev);
        next.delete(featureId);
        return next;
      });
      // Clear the stored invalid link value
      setInvalidLinkValues(prev => {
        const { [featureId]: _omit, ...rest } = prev;
        return rest;
      });

      // Also clear the name validation error if it was set due to invalid link
      setMissingRequired(prev => {
        if (!prev[featureId]?.name) return prev;
        const rest = { ...prev } as any;
        const entry = { ...(rest[featureId] || {}) };
        delete entry.name;
        if (Object.keys(entry).length === 0) {
          delete rest[featureId];
          // Hide validation message if no more errors
          setValidationMsgVisible(false);
        } else {
          rest[featureId] = entry;
        }
        return rest;
      });
    }

    // Clear name error only if a real name was entered (not placeholder)
    if (trimmedName && trimmedName !== '') {
      setMissingRequired(prev => {
        if (!prev[featureId]?.name) return prev;
        const rest = { ...prev } as any;
        const entry = { ...(rest[featureId] || {}) };
        delete entry.name;
        if (Object.keys(entry).length === 0) delete rest[featureId];
        else rest[featureId] = entry;
        return rest;
      });
    }

    // Close the popup after successful save
    setHoverPopup(prev => ({ ...prev, isVisible: false }));
    // Reset hover trigger flag to allow auto-hide logic for other popups
    setIsHoveringTrigger(false);
  };

  // Save handler for PRD/DEMO popup
  const handlePrdDemoSave = (
    featureId: string,
    type: 'prd' | 'demo',
    etaDate: string,
    url: string
  ) => {
    // Validate Demo URL if provided
    let hasInvalidDemoLinkAttempt = false;
    if (type === 'demo' && url && url.trim() !== '') {
      if (!isValidDemoUrl(url.trim())) {
        // Invalid Demo URL - track the attempt
        hasInvalidDemoLinkAttempt = true;
      }
    }

    // Validate PRD URL if provided
    let hasInvalidPrdLinkAttempt = false;
    if (type === 'prd' && url && url.trim() !== '') {
      if (!isValidPrdUrl(url.trim())) {
        hasInvalidPrdLinkAttempt = true;
      }
    }

    setLocalFeatures(prev => {
      const next = prev.map(feature => {
        if (feature.id !== featureId) return feature;

        // Initialize the object if it doesn't exist (for new features)
        const prevObj = feature[type] || {
          etaDate: '',
          url: '',
          available: false
        };
        const updated: FeaturePrdData = { ...prevObj };

        // Only update if value actually changed
        if ((etaDate || '') !== (prevObj.etaDate || '')) {
          updated.etaDate = etaDate || '';
        }

        // Update URL if valid, or clear it if empty
        // For demo, only save if it's a valid Demo URL (Frame.io or Figma)
        if (type === 'demo') {
          if (url && isValidDemoUrl(url)) {
            if ((url || '') !== (prevObj.url || '')) {
              updated.url = url;
            }
          } else if (!url || url.trim() === '') {
            if ((prevObj.url || '') !== '') {
              updated.url = '';
            }
          }
          // If invalid Demo URL, keep original URL (don't save invalid value)
        } else {
          // PRD validation - standard URL format only
          if (url && isValidPrdUrl(url)) {
            if ((url || '') !== (prevObj.url || '')) {
              updated.url = url;
            }
          } else if (!url || url.trim() === '') {
            if ((prevObj.url || '') !== '') {
              updated.url = '';
            }
          }
          // If invalid PRD URL, keep original URL (don't save invalid value)
        }

        // If nothing changed, return original feature (avoid unnecessary persistence)
        if (
          (updated.etaDate || '') === (prevObj.etaDate || '') &&
          (updated.url || '') === (prevObj.url || '')
        ) {
          return feature;
        }

        return { ...feature, [type]: { ...prevObj, ...updated } } as FeatureRow;
      });

      // Persist new rows
      const newRows = next.filter(f => f.id.startsWith('new-feature-'));
      persistNewFeatures(newRows);

      // Persist API rows changes - only when different than original
      if (!featureId.startsWith('new-feature-')) {
        setFeatureChanges(prevChanges => {
          const original = getOriginalFeature(featureId) as any;
          const originalType = (original && original[type]) || {};
          const originalEta = formatEtaForDisplay(originalType?.etaDate || '');
          const originalUrl = originalType?.url || '';

          const nextEta = etaDate || '';
          // For demo, only use valid Demo URLs
          const nextUrl =
            type === 'demo'
              ? url && isValidDemoUrl(url)
                ? url
                : ''
              : url && isValidPrdUrl(url)
              ? url
              : '';

          const isEtaChanged = (originalEta || '') !== (nextEta || '');
          const isUrlChanged = (originalUrl || '') !== (nextUrl || '');

          const nextForRow: any = { ...(prevChanges[featureId] || {}) };
          if (isEtaChanged || isUrlChanged) {
            nextForRow[type] = {
              etaDate: nextEta,
              url: nextUrl,
              available: !!(nextForRow[type]?.available || false)
            };
          } else {
            // Reverted to original; remove this field's changes
            if (nextForRow[type]) delete nextForRow[type];
          }

          // If no keys remain, remove the feature entry
          if (Object.keys(nextForRow).length === 0) {
            const { [featureId]: _omit, ...rest } = prevChanges;
            persistChanges(rest);
            return rest;
          }

          const merged = { ...prevChanges, [featureId]: nextForRow };
          persistChanges(merged);
          return merged;
        });
      }

      return next;
    });

    // Track invalid Demo link attempt for this feature (enables Save button)
    if (hasInvalidDemoLinkAttempt) {
      setInvalidDemoLinkAttempts(prev => {
        const next = new Set(prev);
        next.add(featureId);
        return next;
      });
      // Store the invalid link value so we can show it in the popup with error
      setInvalidDemoLinkValues(prev => ({
        ...prev,
        [featureId]: url.trim()
      }));
    } else {
      // Clear invalid Demo link attempt if link is now valid or empty
      setInvalidDemoLinkAttempts(prev => {
        if (!prev.has(featureId)) return prev;
        const next = new Set(prev);
        next.delete(featureId);
        return next;
      });
      // Clear the stored invalid link value
      setInvalidDemoLinkValues(prev => {
        const { [featureId]: _omit, ...rest } = prev;
        return rest;
      });
    }

    // Track invalid PRD link attempt for this feature (enables Save button)
    if (hasInvalidPrdLinkAttempt) {
      setInvalidPrdLinkAttempts(prev => {
        const next = new Set(prev);
        next.add(featureId);
        return next;
      });
      setInvalidPrdLinkValues(prev => ({
        ...prev,
        [featureId]: url.trim()
      }));
    } else {
      setInvalidPrdLinkAttempts(prev => {
        if (!prev.has(featureId)) return prev;
        const next = new Set(prev);
        next.delete(featureId);
        return next;
      });
      setInvalidPrdLinkValues(prev => {
        const { [featureId]: _omit, ...rest } = prev;
        return rest;
      });
    }

    // Clear DEMO/PRD error only if a value was actually saved
    const hasValue =
      (etaDate && etaDate.trim() !== '') || (url && url.trim() !== '');
    if (hasValue && !hasInvalidDemoLinkAttempt && !hasInvalidPrdLinkAttempt) {
      setMissingRequired(prev => {
        if (!prev[featureId]?.[type]) return prev;
        const rest = { ...prev } as any;
        const entry = { ...(rest[featureId] || {}) };
        delete entry[type];
        if (Object.keys(entry).length === 0) delete rest[featureId];
        else rest[featureId] = entry;
        return rest;
      });
    }
  };

  // Toggle MVP checkbox (allowed for both new and API features)
  const handleToggleBoolean = (
    featureId: string,
    field: 'mvp' | 'genAi' | 'consumesCredit'
  ) => {
    setLocalFeatures(prev => {
      const next = prev.map(feature => {
        if (feature.id !== featureId) return feature;
        const updated: any = { ...feature, [field]: !feature[field] };
        // If GenAI is turned off, force consumesCredit to false as well
        if (field === 'genAi' && !updated.genAi) {
          updated.consumesCredit = false;
        }
        return updated as any;
      });

      // Persist only new rows
      const newRows = next.filter(f => f.id.startsWith('new-feature-'));
      persistNewFeatures(newRows);

      // Record change for API rows so Confirm enables PATCH; clear when equal to original
      const toggled = next.find(f => f.id === featureId);
      if (toggled && !featureId.startsWith('new-feature-')) {
        setFeatureChanges(prevChanges => {
          const original = getOriginalFeature(featureId);
          const nextForRow: any = { ...(prevChanges[featureId] || {}) };

          // Track primary toggle field change
          const isPrimaryChanged =
            !!(toggled as any)[field] !== !!(original as any)?.[field];
          if (isPrimaryChanged) nextForRow[field] = !!(toggled as any)[field];
          else delete nextForRow[field];

          // If genAi was turned off, also record consumesCredit=false so PATCH includes it
          if (field === 'genAi') {
            const isConsumesChanged =
              !!(toggled as any).consumesCredit !==
              !!(original as any)?.consumesCredit;
            if (isConsumesChanged)
              nextForRow.consumesCredit = !!(toggled as any).consumesCredit;
            else delete nextForRow.consumesCredit;
          }

          if (Object.keys(nextForRow).length === 0) {
            const { [featureId]: _omit, ...rest } = prevChanges;
            persistChanges(rest);
            return rest;
          }
          const merged = { ...prevChanges, [featureId]: nextForRow };
          persistChanges(merged);
          return merged;
        });
      }

      return next;
    });
  };
  // Backward compat alias
  const handleToggleMvp = (featureId: string) =>
    handleToggleBoolean(featureId, 'mvp');

  // Add new feature handler
  const handleAddFeature = () => {
    const seedSurfaces =
      defaultSurfacesRef.current.length > 0
        ? defaultSurfacesRef.current
        : Array.isArray(defaultSurfaces)
        ? defaultSurfaces.filter(s => typeof s === 'string' && s.trim() !== '')
        : [];
    const newFeature: FeatureRow = {
      id: `new-feature-${Date.now()}`,
      name: 'Feature Name',
      riskLevel: 'low', // Default to green (low risk)
      demo: undefined, // Start with undefined for new features to show grey icon
      prd: undefined, // Start with undefined for new features to show grey icon
      internalBeta: '', // Empty string = placeholder, will show MM/DD/YY
      privateBeta: '', // Empty string = placeholder, will show MM/DD/YY
      publicBeta: '', // Empty string = placeholder, will show MM/DD/YY
      ga: '', // Empty string = placeholder, will show MM/DD/YY
      surfaces: seedSurfaces,
      mvp: false,
      genAi: false,
      consumesCredit: false
    };

    setLocalFeatures(prev => {
      const next = [...prev, newFeature];
      // Persist only new rows
      const newRows = next.filter(f => f.id.startsWith('new-feature-'));
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(newRows));
      } catch {}
      return next;
    });

    // Call the optional onAddFeature callback
    if (onAddFeature) {
      onAddFeature();
    }
  };

  // Helper to extract default special value for calendar (TBD/N/A)
  const getDefaultSpecialValueForField = (
    featureId: string,
    fieldType: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga'
  ): 'TBD' | 'N/A' | undefined => {
    const feature = localFeatures.find(f => f.id === featureId);
    if (!feature) return undefined;

    const value = feature[fieldType];
    if (!value) return undefined;

    // Check if it's a plain string TBD or N/A
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return undefined; // Empty string = placeholder, no special value

      const upper = trimmed.toUpperCase();
      if (upper === 'TBD') return 'TBD';
      if (upper === 'N/A') return 'N/A';
    }

    // Check if it's a structured special value
    if (typeof value === 'object' && (value as any).type === 'special') {
      const specialVal = (value as any).value;
      if (specialVal === 'TBD' || specialVal === 'N/A') {
        return specialVal;
      }
    }

    return undefined;
  };

  // Helper to get default date for calendar highlighting
  const getDefaultDateForField = (
    featureId: string,
    fieldType: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga'
  ): CalendarDate | undefined => {
    const feature = localFeatures.find(f => f.id === featureId);
    if (!feature) return undefined;

    const value = feature[fieldType];
    if (!value) return undefined;

    // Only parse actual date values (YYYY-MM-DD format)
    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Check if it's in ISO date format (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        try {
          return parseDate(trimmed);
        } catch {
          return undefined;
        }
      }
    }

    return undefined;
  };

  // Helper to get default quarter for calendar highlighting
  const getDefaultQuarterForField = (
    featureId: string,
    fieldType: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga'
  ): { quarter: number; year: number } | undefined => {
    const feature = localFeatures.find(f => f.id === featureId);
    if (!feature) return undefined;

    const value = feature[fieldType];
    if (!value || typeof value !== 'string') return undefined;

    const trimmed = value.trim();
    // Parse "Q2 2026" format
    const match = trimmed.match(/^Q([1-4])\s+(\d{4})$/i);
    if (match) {
      return { quarter: parseInt(match[1], 10), year: parseInt(match[2], 10) };
    }

    return undefined;
  };

  // Helper to get default month for calendar highlighting
  const getDefaultMonthForField = (
    featureId: string,
    fieldType: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga'
  ): { month: string; year: number } | undefined => {
    const feature = localFeatures.find(f => f.id === featureId);
    if (!feature) return undefined;

    const value = feature[fieldType];
    if (!value || typeof value !== 'string') return undefined;

    const trimmed = value.trim();
    // Parse "Feb 2026" or "Sep 2025" format
    const match = trimmed.match(/^([A-Za-z]{3})\s+(\d{4})$/i);
    if (match) {
      return { month: match[1], year: parseInt(match[2], 10) };
    }

    return undefined;
  };

  // Handle calendar popup for beta dates (click only)
  const handleDateCellInteraction = (
    event: React.MouseEvent,
    featureId: string,
    fieldType: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga'
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    // Position relative to overlay root to handle small-screen slideout offsets
    const overlayRoot =
      document.querySelector('[data-testid="slideout-panel"]') ||
      document.querySelector('[data-testid="features-table-card"]') ||
      document.body;
    const rootRect = (overlayRoot as HTMLElement).getBoundingClientRect();
    const popupX = rect.left - rootRect.left;
    const popupY = rect.bottom - rootRect.top + 8;

    setCalendarPopup({
      isVisible: true,
      featureId,
      fieldType,
      position: { x: popupX, y: popupY }
    });
  };

  // Handle click on date cell
  const handleDateCellClick = (
    event: React.MouseEvent,
    featureId: string,
    fieldType: 'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga'
  ) => {
    handleDateCellInteraction(event, featureId, fieldType);
  };

  // Handle date selection from calendar
  const handleDateSelect = (
    date: CalendarDate,
    isQuarterSelection?: boolean,
    selectedQuarter?: { quarter: number; year: number },
    isMonthSelection?: boolean,
    selectedMonth?: { month: string; year: number },
    specialValue?: 'TBD' | 'N/A' | null
  ) => {
    if (!calendarPopup.featureId || !calendarPopup.fieldType) return;

    let dateValue = '';

    // Handle special values (TBD/N/A) - check if parameter was passed
    if (specialValue !== undefined) {
      if (specialValue === null) {
        // User toggled OFF the special value - reset to empty string (placeholder)
        setLocalFeatures(prev => {
          const next = prev.map(f =>
            f.id === calendarPopup.featureId
              ? {
                  ...f,
                  [calendarPopup.fieldType!]: '' // Empty string = placeholder
                }
              : f
          );
          const newRows = next.filter(f => f.id.startsWith('new-feature-'));
          persistNewFeatures(newRows);
          return next;
        });

        // Track change for API features so confirm button becomes blue
        if (
          calendarPopup.featureId &&
          !calendarPopup.featureId.startsWith('new-feature-')
        ) {
          setFeatureChanges(prevChanges => {
            const original = getOriginalFeature(calendarPopup.featureId!);
            const key = calendarPopup.fieldType!;
            const originalVal = (original as any)?.[key];
            const originalDisplay = formatEtaForDisplay(originalVal).trim();
            const isChanged = (originalDisplay || '') !== ''; // Changed from original to empty

            const nextForRow: any = {
              ...(prevChanges[calendarPopup.featureId!] || {})
            };
            if (isChanged) nextForRow[key] = ''; // Track empty string as change
            else delete nextForRow[key];

            if (Object.keys(nextForRow).length === 0) {
              const { [calendarPopup.featureId!]: _omit, ...rest } =
                prevChanges;
              persistChanges(rest);
              return rest;
            }
            const merged = {
              ...prevChanges,
              [calendarPopup.featureId!]: nextForRow
            };
            persistChanges(merged);
            return merged;
          });
        }

        // Close calendar and return early
        setCalendarPopup({
          isVisible: false,
          featureId: null,
          fieldType: null,
          position: { x: 0, y: 0 }
        });
        return;
      } else {
        // User selected TBD or N/A
        dateValue = specialValue;
      }
    } else if (selectedMonth) {
      // Use month selection if available
      dateValue = `${selectedMonth.month} ${selectedMonth.year}`;
    } else if (selectedQuarter) {
      // Use quarter selection if available
      const quarterNames = ['Q1', 'Q2', 'Q3', 'Q4'];
      dateValue = `${quarterNames[selectedQuarter.quarter - 1]} ${
        selectedQuarter.year
      }`;
    } else if (date) {
      // Use exact date in ISO-like format YYYY-MM-DD
      const month = String(date.month).padStart(2, '0');
      const day = String((date as any).day || 1).padStart(2, '0');
      dateValue = `${date.year}-${month}-${day}`;
    }

    // Update the feature with the new date
    setLocalFeatures(prev => {
      const next = prev.map(feature =>
        feature.id === calendarPopup.featureId
          ? {
              ...feature,
              [calendarPopup.fieldType!]: dateValue
            }
          : feature
      );

      // Save new rows
      const newRows = next.filter(f => f.id.startsWith('new-feature-'));
      persistNewFeatures(newRows);

      // Save changes for API rows, and clear when equal to original
      const updatedFeature = next.find(f => f.id === calendarPopup.featureId);
      if (updatedFeature && !updatedFeature.id.startsWith('new-feature-')) {
        setFeatureChanges(prevChanges => {
          const original = getOriginalFeature(calendarPopup.featureId!);
          const key = calendarPopup.fieldType!;
          const originalVal = (original as any)?.[key];
          const originalDisplay = formatEtaForDisplay(originalVal).trim();
          const isChanged = (originalDisplay || '') !== (dateValue || '');

          const nextForRow: any = {
            ...(prevChanges[calendarPopup.featureId!] || {})
          };
          if (isChanged) nextForRow[key] = dateValue;
          else delete nextForRow[key];

          if (Object.keys(nextForRow).length === 0) {
            const { [calendarPopup.featureId!]: _omit, ...rest } = prevChanges;
            persistChanges(rest);
            return rest;
          }
          const merged = {
            ...prevChanges,
            [calendarPopup.featureId!]: nextForRow
          };
          persistChanges(merged);
          return merged;
        });
      }

      return next;
    });

    // After local update, clear validation flag for this field if it was set
    if (calendarPopup.featureId && calendarPopup.fieldType) {
      const fid = calendarPopup.featureId;
      const key = calendarPopup.fieldType;
      setMissingRequired(prev => {
        if (!prev[fid]?.[key]) return prev;
        const rest = { ...prev } as any;
        const entry = { ...(rest[fid] || {}) };
        delete entry[key];
        if (Object.keys(entry).length === 0) delete rest[fid];
        else rest[fid] = entry;
        return rest;
      });
    }

    // Close calendar
    setCalendarPopup({
      isVisible: false,
      featureId: null,
      fieldType: null,
      position: { x: 0, y: 0 }
    });
  };

  // Close calendar when clicking outside
  const handleCalendarClose = () => {
    setCalendarPopup({
      isVisible: false,
      featureId: null,
      fieldType: null,
      position: { x: 0, y: 0 }
    });
  };

  // Determine the portal root element for overlays: prefer slideout panel, then table card, else body
  const getOverlayRootElement = React.useCallback((): Element => {
    const slideout = document.querySelector('[data-testid="slideout-panel"]');
    if (slideout) return slideout as Element;
    const tableCard = document.querySelector(
      '[data-testid="features-table-card"]'
    );
    if (tableCard) return tableCard as Element;
    return document.body as Element;
  }, []);

  const getOverlayRootRect = React.useCallback((): DOMRect => {
    const root = getOverlayRootElement() as HTMLElement;
    return root.getBoundingClientRect();
  }, [getOverlayRootElement]);

  // Force re-render for overlays that compute position in render
  const [overlayRerenderKey, setOverlayRerenderKey] = React.useState(0);

  // Reset transforms when popups open/close
  React.useEffect(() => {
    if (calendarPopupRef.current) {
      calendarPopupRef.current.style.transform = 'translate(0, 0)';
    }
  }, [
    calendarPopup.isVisible,
    calendarPopup.featureId,
    calendarPopup.fieldType
  ]);

  React.useEffect(() => {
    if (hoverPopupRef.current) {
      hoverPopupRef.current.style.transform = 'translate(0, 0)';
    }
  }, [
    hoverPopup.isVisible,
    hoverPopup.content?.featureId,
    hoverPopup.content?.fieldType
  ]);

  React.useEffect(() => {
    if (optionsMenuPopupRef.current) {
      optionsMenuPopupRef.current.style.transform = 'translate(0, 0)';
    }
  }, [optionsMenu.featureId]);

  React.useEffect(() => {
    if (riskDropdownPopupRef.current) {
      riskDropdownPopupRef.current.style.transform = 'translate(0, 0)';
    }
  }, [riskLevelDropdownOpen.featureId]);

  React.useEffect(() => {
    if (surfacesDropdownPopupRef.current) {
      surfacesDropdownPopupRef.current.style.transform = 'translate(0, 0)';
    }
  }, [surfacesDropdownOpen]);

  // Recalculate overlay positions on scroll/resize so they stay anchored to their triggers
  React.useEffect(() => {
    if (
      !calendarPopup.isVisible &&
      !surfacesDropdownOpen &&
      !optionsMenu.featureId &&
      !hoverPopup.isVisible &&
      !riskLevelDropdownOpen.featureId
    ) {
      return;
    }

    const recalcCalendarPosition = () => {
      if (
        !calendarPopup.isVisible ||
        !calendarPopup.featureId ||
        !calendarPopup.fieldType ||
        !calendarPopupRef.current
      )
        return;
      const fid = calendarPopup.featureId;
      const key = calendarPopup.fieldType;
      const selectorMap: any = {
        internalBeta: `[data-testid="features-cell-internal-beta-btn-${fid}"]`,
        privateBeta: `[data-testid=\"features-cell-private-beta-btn-${fid}\"]`,
        publicBeta: `[data-testid=\"features-cell-public-beta-btn-${fid}\"]`,
        ga: `[data-testid=\"features-cell-ga-btn-${fid}\"]`
      };
      const selector = (selectorMap as any)[key];
      const el = selector
        ? (document.querySelector(selector) as HTMLElement | null)
        : null;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const rootRect = getOverlayRootRect();

      // Use CSS transform for instant, smooth position updates (no React re-render)
      const newX = rect.left - rootRect.left;
      const newY = rect.bottom - rootRect.top + 8;
      const deltaX = newX - calendarPopup.position.x;
      const deltaY = newY - calendarPopup.position.y;

      calendarPopupRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    };

    const recalcSurfacesPosition = () => {
      if (!surfacesDropdownOpen || !surfacesDropdownPopupRef.current) return;
      const anchor = surfacesCellRefs.current[surfacesDropdownOpen];
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const rootRect = getOverlayRootRect();

      // Calculate where it should be now
      const newTop = rect.bottom - rootRect.top + 4;
      const newLeft = rect.left - rootRect.left;

      // Get the original position from when it opened
      const currentTop =
        parseFloat(surfacesDropdownPopupRef.current.style.top) || 0;
      const currentLeft =
        parseFloat(surfacesDropdownPopupRef.current.style.left) || 0;

      const deltaX = newLeft - currentLeft;
      const deltaY = newTop - currentTop;

      surfacesDropdownPopupRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    };

    const recalcHoverPopupPosition = () => {
      if (!hoverPopup.isVisible || !hoverPopupRef.current) return;
      // Use the cell container element for stable positioning
      const el =
        (hoverAnchorElRef.current as HTMLElement | null) ||
        (hoverPopup.anchorSelector
          ? (document.querySelector(
              hoverPopup.anchorSelector
            ) as HTMLElement | null)
          : null);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const rootRect = getOverlayRootRect();

      // Calculate cell container's current position
      const cellLeft = rect.left - rootRect.left;
      const cellTop = rect.top - rootRect.top;

      // Apply the offset to maintain the visual position relative to cell content
      const offset = hoverPopup.openOffset || { dx: -55, dy: 40 };
      const newX = cellLeft + offset.dx;
      const newY = cellTop + offset.dy;

      // Use CSS transform for instant, smooth position updates (no React re-render)
      const deltaX = newX - hoverPopup.position.x;
      const deltaY = newY - hoverPopup.position.y;

      hoverPopupRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    };

    const recalcOptionsPosition = () => {
      const fid = optionsMenu.featureId;
      if (!fid || !optionsMenuPopupRef.current) return;
      const btn = document.querySelector(
        `[data-testid=\"features-cell-action-options-${fid}\"]`
      ) as HTMLElement | null;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const rootRect = getOverlayRootRect();

      // Calculate button position now
      const buttonX = rect.right - rootRect.left;
      const buttonY = rect.bottom - rootRect.top + 8;

      // Calculate delta from original button position (stored in optionsMenu.position)
      // The menu position is already calculated with proper width/overflow handling when opened
      const deltaX = buttonX - (optionsAnchorRef.current?.right || buttonX);
      const deltaY = buttonY - optionsMenu.position.y;

      // Use CSS transform for instant, smooth position updates (no React re-render)
      optionsMenuPopupRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;

      // Update anchor ref with current button position
      optionsAnchorRef.current = {
        left: rect.left - rootRect.left,
        right: rect.right - rootRect.left,
        boundaryWidth: rootRect.width
      };
    };

    const recalcRiskPosition = () => {
      const fid = riskLevelDropdownOpen.featureId;
      if (!fid || !riskDropdownPopupRef.current) return;
      // Find the risk button again and recompute
      const btn = document.querySelector(
        `[data-testid=\"features-cell-warning-${fid}\"]`
      ) as HTMLElement | null;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const rootRect = getOverlayRootRect();

      // Use CSS transform for instant, smooth position updates (no React re-render)
      const newX = rect.left - rootRect.left;
      const newY = rect.bottom - rootRect.top + 4;
      const deltaX = newX - riskLevelDropdownOpen.position.x;
      const deltaY = newY - riskLevelDropdownOpen.position.y;

      riskDropdownPopupRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    };

    let rafId: number | null = null;

    const onScrollOrResize = () => {
      // Cancel any pending RAF to avoid stacking updates
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      // Schedule update for next animation frame
      rafId = requestAnimationFrame(() => {
        recalcCalendarPosition();
        recalcSurfacesPosition();
        recalcHoverPopupPosition();
        recalcOptionsPosition();
        recalcRiskPosition();
        rafId = null;
      });
    };

    const containers: (Element | null)[] = [
      window as any,
      document.querySelector('[data-testid="slideout-content-wrapper"]'),
      document.querySelector('[data-testid="slideout-content-container"]'),
      document.querySelector('[data-testid="features-table-card"]'),
      document.querySelector('[data-testid="features-table-body"]')
    ];

    containers.forEach(c => {
      if (!c) return;
      (c as any).addEventListener('scroll', onScrollOrResize, true);
      (c as any).addEventListener('resize', onScrollOrResize, true);
    });

    window.addEventListener('resize', onScrollOrResize);

    return () => {
      // Cancel any pending RAF on cleanup
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      containers.forEach(c => {
        if (!c) return;
        (c as any).removeEventListener('scroll', onScrollOrResize, true);
        (c as any).removeEventListener('resize', onScrollOrResize, true);
      });
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [
    calendarPopup.isVisible,
    calendarPopup.featureId,
    calendarPopup.fieldType,
    surfacesDropdownOpen,
    optionsMenu.featureId,
    hoverPopup.isVisible,
    hoverPopup.content?.featureId,
    hoverPopup.content?.fieldType,
    riskLevelDropdownOpen.featureId
  ]);

  // Update hover popup anchor reference when DOM changes (e.g., when icon appears for first time)
  React.useEffect(() => {
    if (!hoverPopup.isVisible || !hoverPopup.anchorSelector) return;

    // Wait for DOM to update, then refresh the anchor element reference
    const timeoutId = setTimeout(() => {
      if (hoverPopup.anchorSelector) {
        const el = document.querySelector(
          hoverPopup.anchorSelector
        ) as HTMLElement | null;
        if (el) {
          hoverAnchorElRef.current = el;
        }
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [hoverPopup.isVisible, hoverPopup.anchorSelector, localFeatures]);

  // Handle clicks outside calendar popup to close it
  React.useEffect(() => {
    if (!calendarPopup.isVisible) return;

    const handleDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if click is inside the calendar popup
      const calendarElement = document.querySelector(
        '.slideout-calendar-portal'
      );
      if (calendarElement && calendarElement.contains(target)) {
        return; // Don't close if clicking inside calendar
      }

      // Check if click is on a date cell trigger (to prevent closing when opening)
      const isDateCellClick =
        target.closest('[data-testid*="date-cell"]') ||
        target.closest('.date-cell') ||
        target.closest('[data-field-type]');

      if (isDateCellClick) {
        return; // Don't close if clicking on date cell trigger
      }

      // Close the calendar
      handleCalendarClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCalendarClose();
      }
    };

    document.addEventListener('mousedown', handleDocMouseDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [calendarPopup.isVisible]);

  // Handle confirm button click
  const handleRiskLevelAction = (
    riskLevel: 'low' | 'moderate' | 'high' | 'in_planning',
    featureId: string
  ) => {
    setLocalFeatures(prev => {
      const next = prev.map(feature =>
        feature.id === featureId ? { ...feature, riskLevel } : feature
      );

      // Also update changes for API rows
      if (!featureId.startsWith('new-feature-')) {
        setFeatureChanges(prevChanges => {
          const original = getOriginalFeature(featureId);
          const isChanged = (original?.riskLevel || '') !== (riskLevel || '');

          const nextForRow: any = { ...(prevChanges[featureId] || {}) };
          if (isChanged) nextForRow.riskLevel = riskLevel;
          else delete nextForRow.riskLevel;

          if (Object.keys(nextForRow).length === 0) {
            const { [featureId]: _omit, ...rest } = prevChanges;
            persistChanges(rest);
            return rest;
          }
          const merged = { ...prevChanges, [featureId]: nextForRow };
          persistChanges(merged);
          return merged;
        });
      }

      // Persist new features to localStorage
      const newFeatures = next.filter(f => f.id.startsWith('new-feature-'));
      persistNewFeatures(newFeatures);

      return next;
    });

    setRiskLevelDropdownOpen({ featureId: null, position: { x: 0, y: 0 } });
  };

  const handleConfirmFeature = async (featureId: string) => {
    const feature = localFeatures.find(f => f.id === featureId);
    if (!feature) return;

    // Check if there are ANY invalid link attempts (JIRA link, Demo link, or PRD link)
    const hasInvalidJiraLink = invalidLinkAttempts.has(featureId);
    const hasInvalidDemoLink = invalidDemoLinkAttempts.has(featureId);
    const hasInvalidPrdLink = invalidPrdLinkAttempts.has(featureId);

    if (hasInvalidJiraLink || hasInvalidDemoLink || hasInvalidPrdLink) {
      // Show validation error - highlight ALL missing required fields + fields with link issues
      setValidationMsgVisible(true);
      const allMissing = getMissingRequired(feature);
      setMissingRequired(prev => ({
        ...prev,
        [featureId]: {
          ...allMissing,
          ...(hasInvalidJiraLink ? { name: true } : {}), // Highlight name if JIRA link invalid
          ...(hasInvalidDemoLink ? { demo: true } : {}), // Highlight demo if Demo link invalid
          ...(hasInvalidPrdLink ? { prd: true } : {}) // Highlight prd if PRD link invalid
        }
      }));
      return;
    }

    try {
      if (featureId.startsWith('new-feature-')) {
        // New feature: validate on click. If invalid, show message and highlight
        if (!isFeatureComplete(feature)) {
          setValidationMsgVisible(true);
          setMissingRequired(prev => ({
            ...prev,
            [featureId]: getMissingRequired(feature)
          }));
          setValidatedRows(prev => {
            const next = new Set(prev);
            next.add(featureId);
            return next;
          });
          return;
        }
        // Real POST payload
        const payload = buildCreateFeaturePayload(feature, {
          releaseId,
          productId
        });
        const res = await createFeature(payload as any);
        if (!res.success) {
          showToast('negative', res.message || 'Failed to create feature');
          return;
        }

        const createdFeatureId = extractCreatedFeatureId(res.data);

        // Convert draft row in place (same slot) until refetch merges server row.
        const newId = createdFeatureId || featureId;
        dismissedDraftIdsRef.current.add(featureId);
        if (createdFeatureId) {
          promotedFeatureIdsRef.current.add(createdFeatureId);
        }
        const savedRow: FeatureRow = {
          ...feature,
          id: newId,
          status: feature.status || 'ACTIVE'
        };
        const remainingDrafts = loadPersistedNewFeatures().filter(
          p => p.id !== featureId
        );
        persistNewFeatures(remainingDrafts);
        setLocalFeatures(prev =>
          prev.map(f => (f.id === featureId ? savedRow : f))
        );
        setOriginalOverrides(prev => {
          const { [featureId]: _omitDraft, ...rest } = prev;
          return rest;
        });

        setMissingRequired(prev => {
          const rest = { ...prev } as Record<string, (typeof prev)[string]>;
          delete rest[featureId];
          if (newId !== featureId) delete rest[newId];
          return rest;
        });
        setValidatedRows(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          if (newId !== featureId) next.delete(newId);
          return next;
        });
        setInvalidLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          if (newId !== featureId) next.delete(newId);
          return next;
        });
        setInvalidLinkValues(prev => {
          const { [featureId]: _omit1, [newId]: _omit2, ...rest } = prev;
          return rest;
        });
        setInvalidDemoLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          if (newId !== featureId) next.delete(newId);
          return next;
        });
        setInvalidDemoLinkValues(prev => {
          const { [featureId]: _omit1, [newId]: _omit2, ...rest } = prev;
          return rest;
        });
        setInvalidPrdLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          if (newId !== featureId) next.delete(newId);
          return next;
        });
        setInvalidPrdLinkValues(prev => {
          const { [featureId]: _omit1, [newId]: _omit2, ...rest } = prev;
          return rest;
        });
        setValidationMsgVisible(false);
        showToast('positive', 'Feature created successfully');
        onRefreshFeatures?.(res.data);
      } else {
        // API feature: only if there are changes
        if (!isApiFeatureDirty(featureId)) {
          return;
        }

        // Validate that all required fields are still filled for API features
        if (!isFeatureComplete(feature)) {
          setValidationMsgVisible(true);
          setMissingRequired(prev => ({
            ...prev,
            [featureId]: getMissingRequired(feature)
          }));
          setValidatedRows(prev => {
            const next = new Set(prev);
            next.add(featureId);
            return next;
          });
          return;
        }

        const updates = featureChanges[featureId] || {};

        // Normalize beta/GA date fields in updates to PhasePayload format (same as POST)
        const phaseKeys: Array<
          'internalBeta' | 'privateBeta' | 'publicBeta' | 'ga'
        > = ['internalBeta', 'privateBeta', 'publicBeta', 'ga'];
        const normalizedUpdates: any = { ...updates };
        phaseKeys.forEach(key => {
          const rawVal = (updates as any)[key];
          if (rawVal === undefined) return; // not being updated
          // If already a structured payload with type, keep as-is
          if (rawVal && typeof rawVal === 'object' && 'type' in rawVal) return;
          // Otherwise convert string like '2025-09-25' | 'Q4 2025' | 'Dec 2025' | 'N/A' | 'TBD'
          normalizedUpdates[key] = buildPhaseDatePayload(String(rawVal || ''));
        });

        // Normalize PRD/DEMO to { link, date } for PATCH as expected by API
        const toLinkDate = (obj?: { etaDate?: string; url?: string }) => {
          const link =
            obj &&
            obj.url &&
            (isValidUrl(String(obj.url)) || isJiraUrl(String(obj.url)))
              ? obj.url
              : '';
          const date = (obj && obj.etaDate) || '';
          return { link, date };
        };

        if ((updates as any).prd !== undefined) {
          normalizedUpdates.prd = toLinkDate((updates as any).prd);
        }

        // Include boolean flags if present in changes
        if ((updates as any).mvp !== undefined) {
          (normalizedUpdates as any).mvp = !!(updates as any).mvp;
        }
        if ((updates as any).genAi !== undefined) {
          (normalizedUpdates as any).genai = !!(updates as any).genAi;
          delete normalizedUpdates.genAi; // Remove camelCase version to avoid duplicate
        }
        if ((updates as any).consumesCredit !== undefined) {
          (normalizedUpdates as any).consumesCredit = !!(updates as any)
            .consumesCredit;
        }
        if ((updates as any).demo !== undefined) {
          normalizedUpdates.demo = toLinkDate((updates as any).demo);
        }

        // Align field name with POST: jiraLink -> nameLink
        if ((updates as any).jiraLink !== undefined) {
          normalizedUpdates.nameLink = (updates as any).jiraLink || '';
          delete normalizedUpdates.jiraLink;
        }

        // Normalize riskLevel to API labels (Low, Moderate, High) like POST
        if ((updates as any).riskLevel !== undefined) {
          const rawRisk = (updates as any).riskLevel;
          normalizedUpdates.riskLevel =
            String(rawRisk ?? '').trim() === ''
              ? ''
              : riskLevelToApiLabel(rawRisk);
        }

        const res = await updateFeature(featureId, normalizedUpdates as any);
        if (!res.success) {
          showToast('negative', res.message || 'Failed to update feature');
          return;
        }

        // On success, clear changes for this feature and refresh list
        setFeatureChanges(prevChanges => {
          const { [featureId]: _omit, ...rest } = prevChanges;
          persistChanges(rest);
          featureChangesRef.current = rest;
          return rest;
        });

        const patchFeature = extractCreatedFeatureFromResponse(res.data);
        if (patchFeature) {
          setLocalFeatures(prev =>
            prev.map(f =>
              f.id === featureId ? applyPatchFeatureToRow(f, patchFeature) : f
            )
          );
        }

        // Clear any validation state for this row and hide message if clean
        setMissingRequired(prev => {
          const rest = { ...prev } as any;
          delete rest[featureId];
          return rest;
        });
        setValidatedRows(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear invalid link attempt on successful save
        setInvalidLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid link value
        setInvalidLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        // Clear invalid Demo link attempt on successful save
        setInvalidDemoLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid Demo link value
        setInvalidDemoLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        // Clear invalid PRD link attempt on successful save
        setInvalidPrdLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid PRD link value
        setInvalidPrdLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        setValidationMsgVisible(false);
        // Drop override so getOriginalFeature uses the post-refetch API row as baseline
        setOriginalOverrides(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        showToast('positive', 'Feature updated successfully');
        // Refresh features from API to ensure UI is in sync
        onRefreshFeatures?.(res.data);
      }
      // If there are no missing fields for any row, hide the global message
      setValidationMsgVisible(prev => {
        // hide when no rows are flagged
        return Object.keys(missingRequired).length > 0 ? prev : false;
      });
    } catch (error) {
      showToast('negative', 'Unexpected error while saving feature');
    }
  };

  // Kebab menu click -> open options dropdown near the icon
  const handleOptionsIconClick = (e: React.MouseEvent, featureId: string) => {
    // Toggle: If already open for this feature, close it
    if (optionsMenu.featureId === featureId) {
      setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });
      return;
    }

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const offset = 8;
    // Always position relative to slideout/table container for consistency
    const rootRect = getOverlayRootRect();
    // Reuse last measured width so first layout matches real menu size (avoids horizontal snap)
    const approxWidth = optionsMenuWidthRef.current || 200;
    const x = Math.max(8, rect.right - rootRect.left - approxWidth);
    const y = rect.bottom - rootRect.top + offset;
    setOptionsMenu({ featureId, position: { x, y } });
    optionsAnchorRef.current = {
      left: rect.left - rootRect.left,
      right: rect.right - rootRect.left,
      boundaryWidth: rootRect.width
    };
  };

  // Measure menu width and clamp X before the browser paints (avoids visible "slide" from wrong guess)
  React.useLayoutEffect(() => {
    if (!optionsMenu.featureId) return;
    const menuEl = optionsMenuRef.current;
    const anchor = optionsAnchorRef.current;
    if (!menuEl || !anchor) return;

    const menuWidth = menuEl.offsetWidth || 0;
    if (menuWidth === 0) return;
    optionsMenuWidthRef.current = menuWidth;
    const margin = 8;
    // Align right edge of menu to the icon by default
    let desiredX = anchor.right - menuWidth;
    // Clamp within container
    if (desiredX < margin) desiredX = margin;
    if (desiredX + menuWidth > anchor.boundaryWidth - margin) {
      desiredX = Math.max(margin, anchor.boundaryWidth - margin - menuWidth);
    }

    setOptionsMenu(prev => {
      if (!prev.featureId) return prev;
      if (Math.abs(desiredX - prev.position.x) <= 1) return prev;
      return {
        ...prev,
        position: { x: desiredX, y: prev.position.y }
      };
    });
  }, [optionsMenu.featureId]);

  // Confirm delete action from options menu
  const handleConfirmDelete = async (featureId: string) => {
    try {
      if (featureId.startsWith('new-feature-')) {
        // Local-only delete for new items
        setLocalFeatures(prev => {
          const next = prev.filter(f => f.id !== featureId);
          const newRows = next.filter(f => f.id.startsWith('new-feature-'));
          persistNewFeatures(newRows);
          return next;
        });
        // Clear validation state for this row and hide global message if no errors remain
        setMissingRequired(prev => {
          const rest = { ...prev } as any;
          delete rest[featureId];
          return rest;
        });
        setValidatedRows(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear invalid link attempt
        setInvalidLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid link value
        setInvalidLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        // Clear invalid Demo link attempt
        setInvalidDemoLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid Demo link value
        setInvalidDemoLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        // Clear invalid PRD link attempt
        setInvalidPrdLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid PRD link value
        setInvalidPrdLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        setValidationMsgVisible(false);
      } else {
        // Capture feature snapshot BEFORE deletion for audit logging
        const featureToDelete = localFeatures.find(f => f.id === featureId);
        const featureSnapshot = featureToDelete ? { ...featureToDelete } : null;

        // Real DELETE call for API items
        const res = await deleteFeature(featureId);
        if (!res.success) {
          showToast('negative', res.message || 'Failed to delete feature');
          return;
        }

        // Remove from local list and clear any pending changes
        setLocalFeatures(prev => prev.filter(f => f.id !== featureId));
        setFeatureChanges(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          persistChanges(rest);
          return rest;
        });
        setConfirmedFeatures(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear validation state for this row (just in case)
        setMissingRequired(prev => {
          const rest = { ...prev } as any;
          delete rest[featureId];
          return rest;
        });
        setValidatedRows(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear invalid link attempt
        setInvalidLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid link value
        setInvalidLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        // Clear invalid Demo link attempt
        setInvalidDemoLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid Demo link value
        setInvalidDemoLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        // Clear invalid PRD link attempt
        setInvalidPrdLinkAttempts(prev => {
          const next = new Set(prev);
          next.delete(featureId);
          return next;
        });
        // Clear stored invalid PRD link value
        setInvalidPrdLinkValues(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        setValidationMsgVisible(false);
        // Remove original snapshot for this feature (no refetch)
        setOriginalOverrides(prev => {
          const { [featureId]: _omit, ...rest } = prev;
          return rest;
        });
        showToast('positive', 'Feature deleted successfully');
        // Refresh features from API to ensure UI is in sync
        onRefreshFeatures?.(res.data ?? undefined);
      }
    } finally {
      setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });
    }
  };

  // Close options menu on outside click or Escape, handle arrow key navigation
  React.useEffect(() => {
    if (!optionsMenu.featureId) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      const el = optionsMenuRef.current;
      const target = ev.target as HTMLElement;

      // Check if click is on the options button itself (to allow toggle logic to work)
      const optionsButton = document.querySelector(
        `[data-testid="features-cell-action-options-${optionsMenu.featureId}"]`
      );

      // Don't close if clicking inside the menu OR on the options button
      if (
        (el && el.contains(target)) ||
        (optionsButton && optionsButton.contains(target))
      ) {
        return;
      }

      // Click is outside both menu and button - close the menu
      setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });
        return;
      }

      // Arrow key navigation within menu
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        const menu = optionsMenuRef.current;
        if (!menu) return;

        const items = Array.from(
          menu.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])')
        ) as HTMLElement[];

        if (items.length === 0) return;

        const currentIndex = items.findIndex(
          item => item === document.activeElement
        );

        let nextIndex: number;
        if (ev.key === 'ArrowDown') {
          nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        }

        ev.preventDefault();
        items[nextIndex]?.focus();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [optionsMenu.featureId]);

  // Mark feature as complete via PATCH
  const handleMarkAsComplete = async (featureId: string) => {
    if (featureId.startsWith('new-feature-')) {
      // No-op for new, just close menu
      setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });
      return;
    }
    try {
      const res = await updateFeature(featureId, {
        status: STATUS_VALUES.COMPLETE
      } as any);
      if (!res.success) {
        showToast('negative', res.message || 'Failed to mark as complete');
        return;
      }

      // Clear any local changes and update local/original state (no refetch)
      setFeatureChanges(prev => {
        const { [featureId]: _omit, ...rest } = prev;
        persistChanges(rest);
        return rest;
      });
      // Update local row status to COMPLETE
      setLocalFeatures(prev =>
        prev.map(f =>
          f.id === featureId ? { ...f, status: STATUS_VALUES.COMPLETE } : f
        )
      );
      // Update original snapshot to the latest local state
      {
        const current = localFeatures.find(f => f.id === featureId);
        if (current) {
          const updated = {
            ...current,
            status: STATUS_VALUES.COMPLETE
          } as FeatureRow;
          setOriginalOverrides(prev => ({ ...prev, [featureId]: updated }));
        }
      }
      showToast('positive', 'Feature marked as complete');
      // Refresh features from API to ensure UI is in sync
      onRefreshFeatures?.(res.data);
    } finally {
      setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });
    }
  };

  // Mark feature as active via PATCH (reopen completed feature)
  const handleMarkAsActive = async (featureId: string) => {
    if (featureId.startsWith('new-feature-')) {
      // No-op for new, just close menu
      setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });
      return;
    }
    try {
      const res = await updateFeature(featureId, {
        status: STATUS_VALUES.ACTIVE
      } as any);
      if (!res.success) {
        showToast('negative', res.message || 'Failed to mark as active');
        return;
      }

      // Clear any local changes and update local/original state (no refetch)
      setFeatureChanges(prev => {
        const { [featureId]: _omit, ...rest } = prev;
        persistChanges(rest);
        return rest;
      });
      // Update local row status to ACTIVE
      setLocalFeatures(prev =>
        prev.map(f =>
          f.id === featureId ? { ...f, status: STATUS_VALUES.ACTIVE } : f
        )
      );
      // Update original snapshot to the latest local state
      {
        const current = localFeatures.find(f => f.id === featureId);
        if (current) {
          const updated = {
            ...current,
            status: STATUS_VALUES.ACTIVE
          } as FeatureRow;
          setOriginalOverrides(prev => ({ ...prev, [featureId]: updated }));
        }
      }
      showToast('positive', 'Feature marked as active');
      // Refresh features from API to ensure UI is in sync
      onRefreshFeatures?.(res.data);
    } finally {
      setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });
    }
  };

  // Clone saved feature as a new draft row (POST only when user saves)
  const handleCloneFeature = (sourceId: string) => {
    if (sourceId.startsWith('new-feature-')) return;
    const source = localFeatures.find(f => f.id === sourceId);
    if (!source) return;

    setOptionsMenu({ featureId: null, position: { x: 0, y: 0 } });

    const trimmed = (source.name || '').trim();
    const baseName = trimmed || 'Feature';
    const newName = `${baseName}-copy`;
    const newId = `new-feature-${Date.now()}`;

    const cloned: FeatureRow = {
      ...source,
      id: newId,
      name: newName,
      status: STATUS_VALUES.ACTIVE,
      confirmed: false,
      prd: source.prd ? { ...source.prd } : undefined,
      demo: source.demo ? { ...source.demo } : undefined
    };

    setLocalFeatures(prev => {
      const next = [...prev, cloned];
      persistNewFeatures(next.filter(f => f.id.startsWith('new-feature-')));
      return next;
    });

    setPendingCloneFocus({
      id: newId,
      name: newName,
      jiraLink: source.jiraLink
    });
  };

  // Scroll functionality - memoized to prevent infinite re-renders
  const checkScrollPosition = React.useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    // Increased threshold to account for real-world layout issues, borders, padding, etc.
    // Only show scroll chevrons when there's meaningful overflow (at least 20px)
    const MIN_SCROLLABLE_WIDTH = 20; // px - minimum width that warrants showing scroll controls

    // Hide chevrons if the entire table fits within the container
    // Use a larger threshold to avoid false positives from sub-pixel rounding, borders, etc.
    if (maxScrollLeft <= MIN_SCROLLABLE_WIDTH) {
      // Reset any tiny scroll that might have been applied by trackpad/mouse
      if (container.scrollLeft !== 0) {
        container.scrollLeft = 0;
      }
      // Prevent further horizontal scroll when there's effectively no overflow,
      // so the table doesn't appear to "wiggle" left/right on slight gestures.
      if (container.style.overflowX !== 'hidden') {
        container.style.overflowX = 'hidden';
      }
      setShowLeftChevron(false);
      setShowRightChevron(false);
      return;
    }

    // When real horizontal overflow exists, ensure scrolling is enabled.
    if (container.style.overflowX !== 'auto') {
      container.style.overflowX = 'auto';
    }

    // Show chevrons based on scroll position when scrolling is needed
    // Use a small epsilon for scroll position checks (to avoid flicker at boundaries)
    const SCROLL_POSITION_EPSILON = 2;
    const newShowLeftChevron = scrollLeft > SCROLL_POSITION_EPSILON;
    const newShowRightChevron =
      scrollLeft < maxScrollLeft - SCROLL_POSITION_EPSILON;

    // Only update state if values actually changed
    setShowLeftChevron(prev =>
      prev !== newShowLeftChevron ? newShowLeftChevron : prev
    );
    setShowRightChevron(prev =>
      prev !== newShowRightChevron ? newShowRightChevron : prev
    );
  }, []);

  // Debounced version using requestAnimationFrame for better scroll performance
  const debouncedCheckScrollPosition = React.useCallback(() => {
    // Cancel any pending RAF
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    // Schedule new RAF
    rafRef.current = requestAnimationFrame(() => {
      checkScrollPosition();
      rafRef.current = null;
    });
  }, [checkScrollPosition]);

  const scrollLeft = React.useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      if (!scrollContainerRef.current) return;
      const container = scrollContainerRef.current;
      container.scrollTo({
        left: 0,
        behavior
      });
    },
    []
  );

  // After clone: same horizontal reset as the left chevron, then focus name (instant scroll so popup anchors correctly)
  React.useLayoutEffect(() => {
    if (!pendingCloneFocus) return;
    const { id, name, jiraLink } = pendingCloneFocus;
    setPendingCloneFocus(null);

    scrollLeft('auto');

    const rowEl = document.querySelector(
      `[data-testid="features-row-${id}"]`
    ) as HTMLElement | null;
    rowEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    const nameEl = document.querySelector(
      `[data-testid="features-cell-name-text-${id}"]`
    ) as HTMLElement | null;
    if (!nameEl || !hasEditAccess || readonly) return;

    const fakeEvent = {
      currentTarget: nameEl,
      preventDefault: () => {},
      stopPropagation: () => {}
    } as unknown as React.MouseEvent<Element>;
    handleNameClickRef.current(fakeEvent, id, name, jiraLink);
  }, [pendingCloneFocus, hasEditAccess, readonly, scrollLeft]);

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    container.scrollTo({
      left: container.scrollWidth - container.clientWidth,
      behavior: 'smooth'
    });
  };

  const handleChevronClick = (
    e: React.MouseEvent,
    direction: 'left' | 'right'
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (direction === 'left') {
      scrollLeft();
    } else {
      scrollRight();
    }
  };

  const handleChevronMouseEnter = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Hide any existing hover popup when hovering over chevron
    setHoverPopup(prev => ({ ...prev, isVisible: false }));
  };

  // Check scroll position on mount and set up event listeners
  // NOTE: localFeatures.length dependency ensures this effect re-runs when going from 0 to >0 features
  // This is critical because the scroll container doesn't exist when there are no features
  React.useEffect(() => {
    // Skip if no features (table won't be rendered)
    if (localFeatures.length === 0) return;

    const container = scrollContainerRef.current;
    if (container) {
      // Initial check (not debounced)
      checkScrollPosition();

      // Set up event listeners with debounced handler
      container.addEventListener('scroll', debouncedCheckScrollPosition);
      window.addEventListener('resize', debouncedCheckScrollPosition);

      // Set up ResizeObserver to detect when table layout changes
      const resizeObserver = new ResizeObserver(() => {
        debouncedCheckScrollPosition();
      });
      resizeObserver.observe(container);

      return () => {
        // Cleanup event listeners
        container.removeEventListener('scroll', debouncedCheckScrollPosition);
        window.removeEventListener('resize', debouncedCheckScrollPosition);
        resizeObserver.disconnect();

        // Cancel any pending RAF on unmount
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }
  }, [localFeatures.length, checkScrollPosition, debouncedCheckScrollPosition]);

  // Check scroll position when features change
  React.useEffect(() => {
    if (scrollContainerRef.current && localFeatures.length > 0) {
      // Use multiple timing strategies to ensure we catch the table after it's fully rendered
      // This is especially important when adding the first feature to an empty table
      const timeoutId1 = setTimeout(() => checkScrollPosition(), 0);
      const timeoutId2 = setTimeout(() => checkScrollPosition(), 50);
      const timeoutId3 = setTimeout(() => checkScrollPosition(), 150);
      // Longer delay for when table first appears (new row added to empty product)
      const timeoutId4 = setTimeout(() => checkScrollPosition(), 300);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        clearTimeout(timeoutId4);
      };
    }
  }, [localFeatures, checkScrollPosition]);

  // Apply sorting to all features (both API rows and draft rows)
  const sortedFeatures = React.useMemo(() => {
    return sortFeaturesByOption(localFeatures, sortBy);
  }, [localFeatures, sortBy]);

  const setItemsWithPersist = React.useCallback(
    (next: FeatureRow[]) => {
      setLocalFeatures(next);
      persistNewFeatures(next.filter(f => f.id.startsWith('new-feature-')));
    },
    [persistNewFeatures]
  );

  const reorder = useReorderable(
    sortedFeatures,
    setItemsWithPersist,
    r => r.id,
    {
      disabled: !hasEditAccess || readonly,
      rowDataTestIdPrefix: 'features-row-',
      dragImageOffsetX: 24,
      dragImageOffsetY: 25,
      dragGhostMaxWidth: 1300,
      skipKeyboardWhen: () =>
        !!(
          document.querySelector('[data-feature-surfaces-dropdown]') ||
          document.querySelector('[data-feature-risk-dropdown]') ||
          document.querySelector('[data-testid="features-options-menu"]')
        ),
      onPersist: nextItems => {
        onReorderCompleted?.(nextItems);
      }
    }
  );

  return (
    <div className={styles.featuresSection} data-testid="features-section">
      {/* Toasts are handled globally via ToastQueue; removing embedded AlertBanner */}
      {sortedFeatures.length > 0 && (
        <div className={styles.featuresTableWrapper}>
          {/* Left Chevron Button */}
          {showLeftChevron && (
            <button
              data-testid="features-scroll-left-button"
              className={`${styles.chevronButton} ${styles.leftChevron}`}
              onClick={e => handleChevronClick(e, 'left')}
              onMouseEnter={handleChevronMouseEnter}
              onMouseLeave={e => e.stopPropagation()}
              aria-label="Scroll left"
            >
              <img
                src="/icons/chevron-right.svg"
                alt="Scroll left"
                className={styles.chevronIcon}
                style={{ transform: 'rotate(180deg)' }}
              />
            </button>
          )}

          {/* Scrollable Table Container */}
          <div
            ref={scrollContainerRef}
            className={styles.tableCard}
            data-testid="features-table-card"
          >
            <div
              className={styles.tableHeader}
              data-testid="features-table-header"
            >
              <div
                className={styles.colFeature}
                data-testid="features-header-features"
              >
                <span className={styles.featuresHeaderLabel}>
                  <span>FEATURES *</span>
                  <span
                    className={styles.featuresItemCount}
                    data-testid="features-item-count"
                  >
                    {(() => {
                      // Count only API rows (exclude new local rows)
                      const apiCount = rows.filter(
                        r => !String(r.id).startsWith('new-feature-')
                      ).length;
                      // Capitalize only the I in Item/Items
                      return `${apiCount} ${apiCount === 1 ? 'Item' : 'Items'}`;
                    })()}
                  </span>
                </span>
              </div>
              <div
                className={styles.colDemo}
                data-testid="features-header-demo"
              >
                <span className={styles.headerPill}>DEMO *</span>
              </div>
              <div className={styles.colPrd} data-testid="features-header-prd">
                <span className={styles.headerPill}>PRD *</span>
              </div>
              <div
                className={styles.colInternalBeta}
                data-testid="features-header-internal-beta"
              >
                INTERNAL
                <br />
                BETA *
              </div>
              <div
                className={styles.colPrivateBeta}
                data-testid="features-header-private-beta"
              >
                PRIVATE
                <br />
                BETA *
              </div>
              <div
                className={styles.colPublicBeta}
                data-testid="features-header-public-beta"
              >
                PUBLIC
                <br />
                BETA *
              </div>
              <div className={styles.colGa} data-testid="features-header-ga">
                GA *
              </div>
              <div
                className={styles.colSurfaces}
                data-testid="features-header-surfaces"
              >
                SURFACES *
              </div>
              <div className={styles.colMvp} data-testid="features-header-mvp">
                MVP
              </div>
              <div
                className={styles.colMvp}
                data-testid="features-header-genai"
              >
                GEN AI
              </div>
              <div
                className={styles.colMvp}
                data-testid="features-header-consumes-credit"
              >
                USES
                <br />
                CREDITS
              </div>
              <div
                className={styles.colActions}
                data-testid="features-header-actions"
              >
                {/* intentionally left blank */}
              </div>
            </div>

            <div className={styles.tableBody} data-testid="features-table-body">
              {sortedFeatures.map(row => {
                const normalizedStatus = String(
                  (row as any).status || ''
                ).toUpperCase();
                const isCompleted = normalizedStatus === STATUS_VALUES.COMPLETE;
                const hasFeatureWriteAccess = hasEditAccess;
                const rowDisabled = !hasFeatureWriteAccess || isCompleted;
                // (debug logging removed)
                const isHighlighted = activeHighlightId === row.id;
                const gaAlertIssue = getFeatureAlertHighlightIssue(
                  row.id,
                  'ga'
                );
                const demoAlertIssue = getFeatureAlertHighlightIssue(
                  row.id,
                  'demo'
                );
                const prdAlertIssue = getFeatureAlertHighlightIssue(
                  row.id,
                  'prd'
                );
                const isDragging = reorder.draggedId === row.id;
                const isDragOver = reorder.dragOverId === row.id;
                const canDrag =
                  hasEditAccess && !readonly && !isUnsavedDraft(row.id);
                return (
                  <div
                    className={`${styles.tableRow} ${
                      isCompleted ? styles.completedRow : ''
                    } ${isHighlighted ? styles.highlightedRow : ''} ${
                      canDrag ? styles.rowDraggable : ''
                    } ${isDragging ? styles.rowDragging : ''} ${
                      isDragOver ? styles.rowDragOver : ''
                    } ${isUnsavedDraft(row.id) ? styles.unsavedDraftRow : ''}`}
                    key={row.id}
                    data-testid={`features-row-${row.id}`}
                    tabIndex={canDrag ? 0 : -1}
                    aria-label={
                      canDrag
                        ? `Feature: ${row.name}. Shift+Arrow Up or Down to reorder.`
                        : undefined
                    }
                    style={
                      isHighlighted
                        ? {
                            border: '2px solid #0265dc',
                            borderRadius: '4px',
                            backgroundColor: '#f0f7ff' // Light blue background
                          }
                        : undefined
                    }
                    draggable={canDrag}
                    onDragStart={e => reorder.handleDragStart(e, row.id)}
                    onKeyDown={
                      canDrag
                        ? e =>
                            reorder.handleRowKeyDown(e, row.id, sortedFeatures)
                        : undefined
                    }
                    onDragOver={
                      canDrag
                        ? e => reorder.handleDragOver(e, row.id, sortedFeatures)
                        : undefined
                    }
                    onDragLeave={
                      canDrag
                        ? e => reorder.handleDragLeave(e, row.id)
                        : undefined
                    }
                    onDrop={
                      canDrag
                        ? e => reorder.handleDrop(e, row.id, sortedFeatures)
                        : undefined
                    }
                    onDragEnd={reorder.handleDragEnd}
                  >
                    <div
                      className={styles.colFeature}
                      data-testid={`features-cell-name-${row.id}`}
                    >
                      {canDrag && (
                        <span
                          className={styles.dragHandle}
                          aria-hidden
                          data-testid={`features-row-drag-handle-${row.id}`}
                        >
                          <img
                            src={DragHandleIcon}
                            alt=""
                            className={styles.dragHandleIcon}
                            width={12}
                            height={16}
                          />
                        </span>
                      )}
                      <span className={styles.riskButtonWrapper}>
                        <ToolTipWrapper
                          message={getRiskTooltipMessage(
                            row.riskLevel,
                            isCompleted
                          )}
                          inlineFlex={true}
                          isOpen={openRiskTooltip === row.id}
                          onOpenChange={isOpen => {
                            if (!isOpen) setOpenRiskTooltip(null);
                          }}
                          onMouseEnter={() =>
                            handleRiskTooltipMouseEnter(row.id)
                          }
                          onMouseLeave={handleRiskTooltipMouseLeave}
                        >
                          <button
                            ref={el => {
                              if (el) {
                                riskButtonRefs.current[row.id] = el;
                              }
                            }}
                            className={styles.riskLevelButton}
                            onClick={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (rowDisabled) return;
                              // Toggle: close if already open, otherwise calculate position and open
                              if (riskLevelDropdownOpen.featureId === row.id) {
                                setRiskLevelDropdownOpen({
                                  featureId: null,
                                  position: { x: 0, y: 0 }
                                });
                              } else {
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                const rootRect = getOverlayRootRect();
                                const position = {
                                  x: rect.left - rootRect.left,
                                  y: rect.bottom - rootRect.top + 4
                                };
                                setRiskLevelDropdownOpen({
                                  featureId: row.id,
                                  position
                                });
                              }
                            }}
                            disabled={rowDisabled}
                            data-testid={`features-cell-warning-${row.id}`}
                          >
                            <img
                              className={styles.riskIcon}
                              src={getRiskIcon(row.riskLevel, isCompleted)}
                              alt={row.riskLevel || 'Risk'}
                              width={
                                isCompleted
                                  ? 18
                                  : row.riskLevel?.toLowerCase() ===
                                      'moderate' ||
                                    row.riskLevel?.toLowerCase() === 'yellow'
                                  ? 18
                                  : row.riskLevel?.toLowerCase() === 'high' ||
                                    row.riskLevel?.toLowerCase() === 'red'
                                  ? 14
                                  : 16
                              }
                              height={
                                isCompleted
                                  ? 18
                                  : row.riskLevel?.toLowerCase() ===
                                      'moderate' ||
                                    row.riskLevel?.toLowerCase() === 'yellow'
                                  ? 18
                                  : row.riskLevel?.toLowerCase() === 'high' ||
                                    row.riskLevel?.toLowerCase() === 'red'
                                  ? 14
                                  : 16
                              }
                            />
                          </button>
                        </ToolTipWrapper>
                      </span>
                      <div
                        className={`${styles.nameEditWrapper} ${
                          hoverPopup.isVisible &&
                          hoverPopup.content?.featureId === row.id &&
                          (hoverPopup.content as any)?.fieldType === 'name'
                            ? styles.showNameEditIcon
                            : ''
                        }`}
                      >
                        <ToolTipWrapper
                          message={row.name !== 'Feature Name' ? row.name : ''}
                          constrainWidth={true}
                          isOpen={openNameTooltip === row.id}
                          onOpenChange={isOpen => {
                            if (!isOpen) setOpenNameTooltip(null);
                          }}
                          onMouseEnter={() =>
                            handleNameTooltipMouseEnter(row.id)
                          }
                          onMouseLeave={handleNameTooltipMouseLeave}
                        >
                          <a
                            href={
                              row.jiraLink &&
                              row.jiraLink.trim() &&
                              (isValidUrl(row.jiraLink.trim()) ||
                                (typeof isJiraUrl === 'function' &&
                                  isJiraUrl(row.jiraLink.trim())))
                                ? normalizeUrl(row.jiraLink.trim())
                                : undefined
                            }
                            className={`${styles.featureName} ${
                              row.name === 'Feature Name'
                                ? styles.placeholderText
                                : ''
                            } ${
                              missingRequired[row.id]?.name
                                ? styles.errorText
                                : ''
                            } ${
                              row.jiraLink &&
                              row.jiraLink.trim() &&
                              (isValidUrl(row.jiraLink.trim()) ||
                                (typeof isJiraUrl === 'function' &&
                                  isJiraUrl(row.jiraLink.trim())))
                                ? styles.clickableFeatureName
                                : ''
                            }`}
                            tabIndex={rowDisabled ? -1 : 0}
                            onFocus={() => {
                              if (rowDisabled) return;
                              focusDeferred(nameEditButtonRefs.current[row.id]);
                            }}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              // If a valid JIRA link exists, open it in a new tab
                              if (
                                row.jiraLink &&
                                row.jiraLink.trim() &&
                                (isValidUrl(row.jiraLink.trim()) ||
                                  (typeof isJiraUrl === 'function' &&
                                    isJiraUrl(row.jiraLink.trim())))
                              ) {
                                setHoverPopup(prev => ({
                                  ...prev,
                                  isVisible: false
                                }));
                                setIsHoveringTrigger(false);
                                setIsHoveringPopup(false);
                                window.open(
                                  normalizeUrl(row.jiraLink.trim()),
                                  '_blank',
                                  'noopener,noreferrer'
                                );
                                return;
                              }
                              // Otherwise open edit popup
                              if (hasEditAccess && !isCompleted) {
                                handleNameClick(
                                  e,
                                  row.id,
                                  row.name,
                                  row.jiraLink
                                );
                              }
                            }}
                            data-testid={`features-cell-name-text-${row.id}`}
                            style={{
                              textDecoration: 'none',
                              cursor:
                                row.jiraLink &&
                                row.jiraLink.trim() &&
                                (isValidUrl(row.jiraLink.trim()) ||
                                  (typeof isJiraUrl === 'function' &&
                                    isJiraUrl(row.jiraLink.trim())))
                                  ? 'pointer'
                                  : canEdit
                                  ? 'text'
                                  : 'default'
                            }}
                          >
                            {row.name}
                          </a>
                        </ToolTipWrapper>
                        {hasEditAccess && !isCompleted && (
                          <button
                            type="button"
                            className={`${styles.iconButton} ${styles.nameEditIcon}`}
                            aria-label="Edit feature name"
                            ref={el =>
                              (nameEditButtonRefs.current[row.id] = el)
                            }
                            tabIndex={rowDisabled ? -1 : 0}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleNameClick(
                                e,
                                row.id,
                                row.name,
                                row.jiraLink
                              );
                            }}
                            onKeyDown={e => {
                              if (isActivationKey(e)) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleNameClick(
                                  e,
                                  row.id,
                                  row.name,
                                  row.jiraLink
                                );
                              }
                            }}
                            data-testid={`features-cell-name-edit-${row.id}`}
                          >
                            <img
                              src={EditIconGray}
                              alt="Edit"
                              width={14}
                              height={14}
                            />
                          </button>
                        )}
                      </div>
                    </div>
                    <div
                      className={`${styles.colDemo}${
                        demoAlertIssue ? ' alert-field-highlight' : ''
                      }`}
                      data-testid={`features-cell-demo-${row.id}`}
                      {...(demoAlertIssue
                        ? {
                            'data-alert-issue-id': releaseId,
                            'data-alert-field-ref':
                              demoAlertIssue.fieldRef ?? 'null',
                            'data-alert-rule-id': demoAlertIssue.ruleId
                          }
                        : {})}
                      onMouseEnter={() => setHoveredDemoRowId(row.id)}
                      onMouseLeave={() =>
                        setHoveredDemoRowId(prev =>
                          prev === row.id ? null : prev
                        )
                      }
                    >
                      <div className={styles.etaContainer}>
                        {row.demo?.url ? (
                          // Has URL - show blue icon, clickable
                          <a
                            href={normalizeUrl(row.demo.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.iconButton}
                            aria-label="Open demo"
                            data-testid={`features-cell-demo-btn-${row.id}`}
                            onClick={e => e.stopPropagation()}
                            style={
                              missingRequired[row.id]?.demo
                                ? {
                                    border: '1px solid #d32f2f',
                                    borderRadius: '3px',
                                    padding: '2px'
                                  }
                                : undefined
                            }
                          >
                            <img
                              src={DemoIcon}
                              alt="Demo"
                              width={12}
                              height={12}
                              data-testid={`features-cell-demo-icon-${row.id}`}
                            />
                          </a>
                        ) : hasEtaAny(row.demo) ? (
                          // Has date but no URL - show date text (no wrap)
                          <span
                            className={`${styles.demoEtaLabel} ${
                              missingRequired[row.id]?.demo
                                ? styles.errorText
                                : ''
                            }`}
                            data-testid={`features-cell-demo-eta-${row.id}`}
                            tabIndex={rowDisabled ? -1 : 0}
                            onFocus={() => {
                              if (rowDisabled) return;
                              setHoveredDemoRowId(row.id);
                              focusDeferred(demoEditButtonRefs.current[row.id]);
                            }}
                          >
                            {formatEtaForDisplay(row.demo?.etaDate || '')}
                          </span>
                        ) : (
                          // No data (no URL and no date): show placeholder and open popup on click
                          <span
                            className={`${styles.demoEtaLabel} ${
                              missingRequired[row.id]?.demo
                                ? styles.errorText
                                : styles.placeholder
                            }`}
                            data-testid={`features-cell-demo-eta-placeholder-${row.id}`}
                            ref={el =>
                              (demoPlaceholderRefs.current[row.id] = el)
                            }
                            onClick={e => {
                              if (rowDisabled) return;
                              e.preventDefault();
                              e.stopPropagation();
                              handleMouseEnter(
                                e,
                                'demo',
                                row.demo || {},
                                row.id
                              );
                            }}
                            tabIndex={rowDisabled ? -1 : 0}
                            onFocus={e => {
                              if (rowDisabled) return;
                              setHoveredDemoRowId(row.id);
                              // Don't defer focus - keep it on placeholder for new rows
                            }}
                            onKeyDown={e => {
                              if (isActivationKey(e)) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMouseEnter(
                                  e,
                                  'demo',
                                  row.demo || {},
                                  row.id
                                );
                              }
                            }}
                          >
                            MM/DD/YY
                          </span>
                        )}
                        {hasEditAccess &&
                          !isCompleted &&
                          (row.demo?.url || hasEtaAny(row.demo)) &&
                          (hoveredDemoRowId === row.id ||
                            focusedDemoEditRowId === row.id ||
                            (hoverPopup.isVisible &&
                              hoverPopup.content?.featureId === row.id &&
                              (hoverPopup.content as any)?.fieldType ===
                                'demo')) && (
                            <button
                              type="button"
                              className={styles.iconButton}
                              aria-label="Edit demo"
                              ref={el =>
                                (demoEditButtonRefs.current[row.id] = el)
                              }
                              tabIndex={rowDisabled ? -1 : 0}
                              onFocus={() => {
                                setHoveredDemoRowId(row.id);
                                setFocusedDemoEditRowId(row.id);
                              }}
                              onBlur={() => {
                                setFocusedDemoEditRowId(null);
                              }}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMouseEnter(
                                  e,
                                  'demo',
                                  row.demo || {},
                                  row.id
                                );
                              }}
                              onKeyDown={e => {
                                if (isActivationKey(e)) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleMouseEnter(
                                    e,
                                    'demo',
                                    row.demo || {},
                                    row.id
                                  );
                                }
                              }}
                              data-testid={`features-cell-demo-edit-${row.id}`}
                            >
                              <img
                                src={EditIconGray}
                                alt="Edit"
                                width={14}
                                height={14}
                              />
                            </button>
                          )}
                      </div>
                    </div>
                    <div
                      className={`${styles.colPrd}${
                        prdAlertIssue ? ' alert-field-highlight' : ''
                      }`}
                      data-testid={`features-cell-prd-${row.id}`}
                      {...(prdAlertIssue
                        ? {
                            'data-alert-issue-id': releaseId,
                            'data-alert-field-ref':
                              prdAlertIssue.fieldRef ?? 'null',
                            'data-alert-rule-id': prdAlertIssue.ruleId
                          }
                        : {})}
                      onMouseEnter={() => setHoveredPrdRowId(row.id)}
                      onMouseLeave={() =>
                        setHoveredPrdRowId(prev =>
                          prev === row.id ? null : prev
                        )
                      }
                    >
                      <div className={styles.etaContainer}>
                        {row.prd?.url ? (
                          // Has URL - show blue icon, clickable
                          <a
                            href={normalizeUrl(row.prd.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.iconButton}
                            aria-label="Open PRD"
                            data-testid={`features-cell-prd-btn-${row.id}`}
                            onClick={e => e.stopPropagation()}
                          >
                            <img
                              src={PrdIcon}
                              alt="PRD"
                              width={11}
                              height={14}
                              data-testid={`features-cell-prd-icon-${row.id}`}
                            />
                          </a>
                        ) : hasEtaAny(row.prd) ? (
                          // Has date but no URL - show date text (no wrap)
                          <span
                            className={`${styles.prdEtaLabel} ${
                              missingRequired[row.id]?.prd
                                ? styles.errorText
                                : ''
                            }`}
                            data-testid={`features-cell-prd-eta-${row.id}`}
                            tabIndex={rowDisabled ? -1 : 0}
                            onFocus={() => {
                              if (rowDisabled) return;
                              setHoveredPrdRowId(row.id);
                              focusDeferred(prdEditButtonRefs.current[row.id]);
                            }}
                          >
                            {formatEtaForDisplay(row.prd?.etaDate || '')}
                          </span>
                        ) : (
                          // No data (no URL and no date): show placeholder and open popup on click
                          <span
                            className={`${styles.prdEtaLabel} ${
                              missingRequired[row.id]?.prd
                                ? styles.errorText
                                : styles.placeholder
                            }`}
                            data-testid={`features-cell-prd-eta-placeholder-${row.id}`}
                            ref={el =>
                              (prdPlaceholderRefs.current[row.id] = el)
                            }
                            onClick={e => {
                              if (rowDisabled) return;
                              e.preventDefault();
                              e.stopPropagation();
                              handleMouseEnter(e, 'prd', row.prd || {}, row.id);
                            }}
                            tabIndex={rowDisabled ? -1 : 0}
                            onFocus={() => {
                              if (rowDisabled) return;
                              setHoveredPrdRowId(row.id);
                              // Don't defer focus - keep it on placeholder for new rows
                            }}
                            onKeyDown={e => {
                              if (isActivationKey(e)) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMouseEnter(
                                  e,
                                  'prd',
                                  row.prd || {},
                                  row.id
                                );
                              }
                            }}
                          >
                            MM/DD/YY
                          </span>
                        )}
                        {hasEditAccess &&
                          !isCompleted &&
                          (row.prd?.url || hasEtaAny(row.prd)) &&
                          (hoveredPrdRowId === row.id ||
                            focusedPrdEditRowId === row.id ||
                            (hoverPopup.isVisible &&
                              hoverPopup.content?.featureId === row.id &&
                              (hoverPopup.content as any)?.fieldType ===
                                'prd')) && (
                            <button
                              type="button"
                              className={styles.iconButton}
                              aria-label="Edit PRD"
                              ref={el =>
                                (prdEditButtonRefs.current[row.id] = el)
                              }
                              tabIndex={rowDisabled ? -1 : 0}
                              onFocus={() => {
                                setHoveredPrdRowId(row.id);
                                setFocusedPrdEditRowId(row.id);
                              }}
                              onBlur={() => {
                                setFocusedPrdEditRowId(null);
                              }}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMouseEnter(
                                  e,
                                  'prd',
                                  row.prd || {},
                                  row.id
                                );
                              }}
                              onKeyDown={e => {
                                if (isActivationKey(e)) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleMouseEnter(
                                    e,
                                    'prd',
                                    row.prd || {},
                                    row.id
                                  );
                                }
                              }}
                              data-testid={`features-cell-prd-edit-${row.id}`}
                            >
                              <img
                                src={EditIconGray}
                                alt="Edit"
                                width={14}
                                height={14}
                              />
                            </button>
                          )}
                      </div>
                    </div>
                    <div
                      className={styles.colInternalBeta}
                      data-testid={`features-cell-internal-beta-${row.id}`}
                    >
                      <button
                        type="button"
                        className={`${styles.dateCell} ${
                          !row.internalBeta || row.internalBeta.trim() === ''
                            ? styles.placeholder
                            : ''
                        } ${
                          missingRequired[row.id]?.internalBeta
                            ? styles.errorText
                            : ''
                        }`}
                        ref={el => (internalBetaBtnRefs.current[row.id] = el)}
                        onClick={e => {
                          if (rowDisabled) return;
                          handleDateCellClick(e, row.id, 'internalBeta');
                        }}
                        disabled={rowDisabled}
                        data-testid={`features-cell-internal-beta-btn-${row.id}`}
                      >
                        {!row.internalBeta || row.internalBeta.trim() === ''
                          ? 'MM/DD/YY'
                          : safePhaseLabel(row.internalBeta)}
                      </button>
                    </div>
                    <div
                      className={styles.colPrivateBeta}
                      data-testid={`features-cell-private-beta-${row.id}`}
                    >
                      <button
                        type="button"
                        className={`${styles.dateCell} ${
                          !row.privateBeta || row.privateBeta.trim() === ''
                            ? styles.placeholder
                            : ''
                        } ${
                          missingRequired[row.id]?.privateBeta
                            ? styles.errorText
                            : ''
                        }`}
                        onClick={e => {
                          if (rowDisabled) return;
                          handleDateCellClick(e, row.id, 'privateBeta');
                        }}
                        disabled={rowDisabled}
                        data-testid={`features-cell-private-beta-btn-${row.id}`}
                      >
                        {!row.privateBeta || row.privateBeta.trim() === ''
                          ? 'MM/DD/YY'
                          : safePhaseLabel(row.privateBeta)}
                      </button>
                    </div>
                    <div
                      className={styles.colPublicBeta}
                      data-testid={`features-cell-public-beta-${row.id}`}
                    >
                      <button
                        type="button"
                        className={`${styles.dateCell} ${
                          !row.publicBeta || row.publicBeta.trim() === ''
                            ? styles.placeholder
                            : ''
                        } ${
                          missingRequired[row.id]?.publicBeta
                            ? styles.errorText
                            : ''
                        }`}
                        onClick={e => {
                          if (rowDisabled) return;
                          handleDateCellClick(e, row.id, 'publicBeta');
                        }}
                        disabled={rowDisabled}
                        data-testid={`features-cell-public-beta-btn-${row.id}`}
                      >
                        {!row.publicBeta || row.publicBeta.trim() === ''
                          ? 'MM/DD/YY'
                          : safePhaseLabel(row.publicBeta)}
                      </button>
                    </div>
                    <div
                      className={`${styles.colGa}${
                        gaAlertIssue ? ' alert-field-highlight' : ''
                      }`}
                      data-testid={`features-cell-ga-${row.id}`}
                      {...(gaAlertIssue
                        ? {
                            'data-alert-issue-id': releaseId,
                            'data-alert-field-ref':
                              gaAlertIssue.fieldRef ?? 'null',
                            'data-alert-rule-id': gaAlertIssue.ruleId
                          }
                        : {})}
                    >
                      <button
                        type="button"
                        className={`${styles.dateCell} ${
                          !row.ga || row.ga.trim() === ''
                            ? styles.placeholder
                            : ''
                        } ${
                          missingRequired[row.id]?.ga ? styles.errorText : ''
                        }`}
                        onClick={e => {
                          if (rowDisabled) return;
                          handleDateCellClick(e, row.id, 'ga');
                        }}
                        disabled={rowDisabled}
                        data-testid={`features-cell-ga-btn-${row.id}`}
                      >
                        {!row.ga || row.ga.trim() === ''
                          ? 'MM/DD/YY'
                          : safePhaseLabel(row.ga)}
                      </button>
                    </div>
                    <div
                      className={styles.colSurfaces}
                      data-testid={`features-cell-surfaces-${row.id}`}
                      ref={el => {
                        if (el) surfacesCellRefs.current[row.id] = el;
                      }}
                    >
                      <TooltipTrigger delay={300}>
                        <ActionButton
                          isQuiet
                          UNSAFE_style={{
                            all: 'unset',
                            cursor: rowDisabled ? 'default' : 'pointer',
                            width: '100%',
                            display: 'block',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            font: 'inherit',
                            color: 'inherit',
                            padding: 0,
                            margin: 0
                          }}
                          onPress={() => {
                            if (rowDisabled) return;
                            if (!surfacesOptionsRef.current[row.id]) {
                              const baseOptions = Array.isArray(allSurfaces)
                                ? allSurfaces
                                : [];
                              const options = Array.from(
                                new Set(
                                  baseOptions.filter(
                                    s =>
                                      typeof s === 'string' && s.trim() !== ''
                                  )
                                )
                              );
                              surfacesOptionsRef.current[row.id] = options;
                            }
                            const hasOptions =
                              Array.isArray(
                                surfacesOptionsRef.current[row.id]
                              ) &&
                              surfacesOptionsRef.current[row.id].length > 0;
                            setSurfacesDropdownOpen(prev =>
                              prev === row.id
                                ? null
                                : hasOptions
                                ? row.id
                                : null
                            );
                          }}
                          aria-label={`Surfaces: ${
                            Array.isArray(row.surfaces)
                              ? row.surfaces.join(', ')
                              : 'None'
                          }`}
                        >
                          <span
                            className={
                              missingRequired[row.id]?.surfaces
                                ? styles.errorText
                                : undefined
                            }
                          >
                            {row.id.startsWith('new-feature-')
                              ? `${(row.surfaces || []).length} Selected`
                              : formatSurfaces(row.surfaces)}
                          </span>
                        </ActionButton>
                        <Tooltip>
                          {Array.isArray(row.surfaces) &&
                          row.surfaces.length > 0
                            ? row.surfaces.join(', ')
                            : 'No surfaces selected'}
                        </Tooltip>
                      </TooltipTrigger>
                    </div>
                    <div
                      className={styles.colMvp}
                      data-testid={`features-cell-mvp-${row.id}`}
                    >
                      <label
                        className={styles.checkboxLabel}
                        data-testid={`features-cell-mvp-label-${row.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!row.mvp}
                          onChange={() => handleToggleBoolean(row.id, 'mvp')}
                          disabled={rowDisabled}
                          data-testid={`features-cell-mvp-input-${row.id}`}
                        />
                        <span
                          className={styles.checkboxVisual}
                          data-testid={`features-cell-mvp-visual-${row.id}`}
                        />
                      </label>
                    </div>
                    <div
                      className={styles.colGenAi}
                      data-testid={`features-cell-genai-${row.id}`}
                    >
                      <label
                        className={styles.checkboxLabel}
                        data-testid={`features-cell-genai-label-${row.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!row.genAi}
                          onChange={() => handleToggleBoolean(row.id, 'genAi')}
                          disabled={rowDisabled}
                          data-testid={`features-cell-genai-input-${row.id}`}
                        />
                        <span
                          className={styles.checkboxVisual}
                          data-testid={`features-cell-genai-visual-${row.id}`}
                        />
                      </label>
                    </div>
                    <div
                      className={styles.colConsumesCredit}
                      data-testid={`features-cell-consumes-credit-${row.id}`}
                    >
                      <label
                        className={styles.checkboxLabel}
                        data-testid={`features-cell-consumes-credit-label-${row.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!row.consumesCredit}
                          onChange={() =>
                            handleToggleBoolean(row.id, 'consumesCredit')
                          }
                          disabled={rowDisabled || !row.genAi}
                          data-testid={`features-cell-consumes-credit-input-${row.id}`}
                        />
                        <span
                          className={styles.checkboxVisual}
                          data-testid={`features-cell-consumes-credit-visual-${row.id}`}
                        />
                      </label>
                    </div>
                    <div
                      className={styles.colActions}
                      data-testid={`features-cell-actions-${row.id}`}
                    >
                      <button
                        type="button"
                        className={styles.saveButton}
                        aria-label="Save"
                        onClick={() =>
                          hasEditAccess &&
                          !isCompleted &&
                          isConfirmIconBlue(row) &&
                          handleConfirmFeature(row.id)
                        }
                        disabled={
                          !hasEditAccess ||
                          isCompleted ||
                          !isConfirmIconBlue(row)
                        }
                        data-testid={`features-cell-action-save-${row.id}`}
                      >
                        Save
                      </button>
                      <button
                        className={styles.actionButton}
                        aria-label="Options"
                        data-testid={`features-cell-action-options-${row.id}`}
                        onClick={e =>
                          hasEditAccess && handleOptionsIconClick(e, row.id)
                        }
                        disabled={!hasEditAccess}
                      >
                        <img
                          src={OptionsIcon}
                          alt="Options"
                          width={16}
                          height={16}
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Chevron Button */}
          {showRightChevron && (
            <button
              data-testid="features-scroll-right-button"
              className={`${styles.chevronButton} ${styles.rightChevron}`}
              onClick={e => handleChevronClick(e, 'right')}
              onMouseEnter={handleChevronMouseEnter}
              onMouseLeave={e => e.stopPropagation()}
              aria-label="Scroll right"
            >
              <img
                src="/icons/chevron-right.svg"
                alt="Scroll right"
                className={styles.chevronIcon}
              />
            </button>
          )}
        </div>
      )}

      <div className={styles.featuresFooterRow} data-testid="features-footer">
        <div className={styles.featuresFooterStart}>
          {!readonly && (
            <button
              type="button"
              className={styles.addButton}
              onClick={handleAddFeature}
              disabled={!(canEdit || isAdmin)}
              data-testid="features-add-button"
            >
              <span data-testid="features-add-label">Add Feature</span>
              <img
                src={AddIcon}
                alt="Add"
                width={8}
                height={8}
                className={styles.addIcon}
                data-testid="features-add-icon"
              />
            </button>
          )}
        </div>
        {validationMsgVisible && (
          <div
            className={styles.featuresFooterEnd}
            data-testid="features-validation-row"
          >
            <span
              className={styles.requiredMessage}
              data-testid="features-required-msg"
            >
              * All required fields must be completed.
            </span>
          </div>
        )}
      </div>

      {/* Hover Popup */}
      <FeatureHoverPopup
        key={`${hoverPopup.content?.featureId}-${hoverPopup.content?.fieldType}`} // Force re-mount when switching
        content={
          liveHoverPopupContent || { fieldType: 'demo', etaDate: '', url: '' }
        }
        isVisible={hoverPopup.isVisible}
        position={hoverPopup.position}
        arrowDirection={hoverPopup.arrowDirection}
        popupRef={hoverPopupRef}
        onMouseEnter={handlePopupMouseEnter}
        onMouseLeave={handlePopupMouseLeave}
        onRequestClose={() => {
          setHoverPopup(prev => ({ ...prev, isVisible: false }));
          setIsHoveringTrigger(false);
          setIsHoveringPopup(false);
        }}
        onTabOut={direction => {
          const featureId = hoverPopup.content?.featureId;
          const fieldType = hoverPopup.content?.fieldType;

          // Close the popup
          setHoverPopup(prev => ({ ...prev, isVisible: false }));
          setIsHoveringTrigger(false);
          setIsHoveringPopup(false);

          // Move focus to appropriate next field
          if (direction === 'next') {
            if (fieldType === 'name' && featureId) {
              // Tab from name popup → move to demo field
              const feature = localFeatures.find(f => f.id === featureId);
              const hasDemoData =
                feature?.demo?.url || hasEtaAny(feature?.demo);

              setTimeout(() => {
                if (hasDemoData) {
                  // Has data - focus edit icon if it exists
                  focusDeferred(demoEditButtonRefs.current[featureId]);
                  setHoveredDemoRowId(featureId);
                } else {
                  // No data - focus the placeholder
                  const placeholder = document.querySelector(
                    `[data-testid="features-cell-demo-eta-placeholder-${featureId}"]`
                  ) as HTMLElement;
                  if (placeholder) placeholder.focus();
                }
              }, 0);
            } else if (fieldType === 'demo' && featureId) {
              // Tab from demo popup → move to prd field
              const feature = localFeatures.find(f => f.id === featureId);
              const hasPrdData = feature?.prd?.url || hasEtaAny(feature?.prd);

              setTimeout(() => {
                if (hasPrdData) {
                  // Has data - focus edit icon if it exists
                  focusDeferred(prdEditButtonRefs.current[featureId]);
                  setHoveredPrdRowId(featureId);
                } else {
                  // No data - focus the placeholder
                  const placeholder = document.querySelector(
                    `[data-testid="features-cell-prd-eta-placeholder-${featureId}"]`
                  ) as HTMLElement;
                  if (placeholder) placeholder.focus();
                }
              }, 0);
            } else if (fieldType === 'prd' && featureId) {
              // Tab from prd popup → move to Internal Beta field
              setTimeout(() => {
                const internalBetaBtn = document.querySelector(
                  `[data-testid="features-cell-internal-beta-btn-${featureId}"]`
                ) as HTMLElement;
                if (internalBetaBtn) internalBetaBtn.focus();
              }, 0);
            }
          } else if (direction === 'prev') {
            if (fieldType === 'demo' && featureId) {
              // Shift+Tab from demo popup → move to name edit button
              setTimeout(() => {
                focusDeferred(nameEditButtonRefs.current[featureId]);
              }, 0);
            } else if (fieldType === 'prd' && featureId) {
              // Shift+Tab from prd popup → move to demo field
              const feature = localFeatures.find(f => f.id === featureId);
              const hasDemoData =
                feature?.demo?.url || hasEtaAny(feature?.demo);

              setTimeout(() => {
                if (hasDemoData) {
                  // Has data - focus edit icon if it exists
                  focusDeferred(demoEditButtonRefs.current[featureId]);
                  setHoveredDemoRowId(featureId);
                } else {
                  // No data - focus the placeholder
                  const placeholder = document.querySelector(
                    `[data-testid="features-cell-demo-eta-placeholder-${featureId}"]`
                  ) as HTMLElement;
                  if (placeholder) placeholder.focus();
                }
              }, 0);
            }
          }
        }}
      />

      {/* Calendar Popup for Beta Dates - Using Portal for visibility */}
      {calendarPopup.isVisible &&
        createPortal(
          <div
            ref={calendarPopupRef}
            className="slideout-calendar-portal"
            data-testid="features-beta-calendar-portal"
            style={{
              position: 'fixed',
              top: calendarPopup.position.y,
              left: calendarPopup.position.x,
              maxHeight: 'none',
              height: 'auto',
              overflow: 'visible',
              zIndex: 1050,
              willChange: 'transform'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                maxHeight: 'none',
                height: 'auto',
                overflow: 'visible'
              }}
            >
              <CalendarRange
                onCalendarDateChange={handleDateSelect}
                mode="single"
                skipTriggerButton={true}
                hideYearList={true}
                showTBDNA={true}
                disableTBD={true}
                disablePastDate={false}
                defaultDate={
                  calendarPopup.featureId && calendarPopup.fieldType
                    ? getDefaultDateForField(
                        calendarPopup.featureId,
                        calendarPopup.fieldType
                      )
                    : undefined
                }
                defaultQuarter={
                  calendarPopup.featureId && calendarPopup.fieldType
                    ? getDefaultQuarterForField(
                        calendarPopup.featureId,
                        calendarPopup.fieldType
                      )
                    : undefined
                }
                defaultMonth={
                  calendarPopup.featureId && calendarPopup.fieldType
                    ? getDefaultMonthForField(
                        calendarPopup.featureId,
                        calendarPopup.fieldType
                      )
                    : undefined
                }
                defaultSpecialValue={
                  calendarPopup.featureId && calendarPopup.fieldType
                    ? getDefaultSpecialValueForField(
                        calendarPopup.featureId,
                        calendarPopup.fieldType
                      )
                    : undefined
                }
                data-testid="features-beta-calendar"
              />
            </div>
          </div>,
          getOverlayRootElement()
        )}

      {/* risk dropdown rendered inline near the trigger */}

      {/* Surfaces Dropdown Portal (hover) */}
      {surfacesDropdownOpen &&
        (() => {
          const FeatureSurfacesDropdownPortal = ({
            featureId
          }: {
            featureId: string;
          }) => {
            const anchor = surfacesCellRefs.current[featureId];
            if (!anchor) return null;

            const rect = anchor.getBoundingClientRect();
            // Position relative to overlay root (slideout/table card) for small screens
            const rootRect = getOverlayRootRect();
            const position = {
              top: rect.bottom - rootRect.top + 4,
              left: rect.left - rootRect.left
            };

            const currentFeature = localFeatures.find(f => f.id === featureId);
            const selected = Array.isArray(currentFeature?.surfaces)
              ? currentFeature!.surfaces!
              : [];
            const options = surfacesOptionsRef.current[featureId] || selected;

            const isAllSelected =
              Array.isArray(options) && selected.length === options.length;

            const updateSurfaces = (newSelected: string[]) => {
              setLocalFeatures(prev => {
                const next = prev.map(f =>
                  f.id === featureId ? { ...f, surfaces: newSelected } : f
                );

                // Persist new rows
                const newRows = next.filter(r =>
                  r.id.startsWith('new-feature-')
                );
                persistNewFeatures(newRows);

                // Persist API rows changes with revert-to-original handling
                if (!featureId.startsWith('new-feature-')) {
                  setFeatureChanges(prevChanges => {
                    const original = getOriginalFeature(featureId);
                    const isChanged = !arraysEqualUnordered(
                      original?.surfaces,
                      newSelected
                    );

                    const nextForRow: any = {
                      ...(prevChanges[featureId] || {})
                    };
                    if (isChanged) nextForRow.surfaces = newSelected;
                    else delete nextForRow.surfaces;

                    if (Object.keys(nextForRow).length === 0) {
                      const { [featureId]: _omit, ...rest } = prevChanges;
                      persistChanges(rest);
                      return rest;
                    }
                    const merged = { ...prevChanges, [featureId]: nextForRow };
                    persistChanges(merged);
                    return merged;
                  });
                }

                return next;
              });
            };

            const toggleOption = (opt: string) => {
              if (opt === '__ALL__') {
                updateSurfaces(isAllSelected ? [] : [...options]);
                return;
              }
              const set = new Set(selected);
              if (set.has(opt)) set.delete(opt);
              else set.add(opt);
              updateSurfaces(Array.from(set));
            };

            return createPortal(
              <div
                ref={surfacesDropdownPopupRef}
                data-feature-surfaces-dropdown={featureId}
                data-testid={`features-surfaces-dropdown-${featureId}`}
                style={{
                  position: 'fixed',
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  zIndex: 1050,
                  width: '235px',
                  minWidth: '235px',
                  willChange: 'transform'
                }}
                onClick={e => e.stopPropagation()}
              >
                <div
                  className={styles.riskLevelDropdownMenu}
                  data-testid="features-surfaces-dropdown-menu"
                  role="menu"
                >
                  <div
                    className={styles.riskLevelMenuItem}
                    data-testid="features-surfaces-item-all"
                    tabIndex={0}
                    role="menuitem"
                    onMouseDown={e => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleOption('__ALL__');
                    }}
                    onKeyDown={e => {
                      if (isActivationKey(e)) {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleOption('__ALL__');
                      }
                    }}
                  >
                    <div className={styles.riskLevelMenuItemContent}>
                      <span>All</span>
                    </div>
                    {isAllSelected && (
                      <BlackCheckMarkIcon width={18} height={16} />
                    )}
                  </div>

                  {options.map(opt => (
                    <div
                      key={opt}
                      className={styles.riskLevelMenuItem}
                      data-testid={`features-surfaces-item-${encodeURIComponent(
                        opt
                      )}`}
                      tabIndex={0}
                      role="menuitem"
                      onMouseDown={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleOption(opt);
                      }}
                      onKeyDown={e => {
                        if (isActivationKey(e)) {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleOption(opt);
                        }
                      }}
                    >
                      <div className={styles.riskLevelMenuItemContent}>
                        <span>{opt}</span>
                      </div>
                      {selected.includes(opt) && (
                        <BlackCheckMarkIcon width={18} height={16} />
                      )}
                    </div>
                  ))}
                </div>
              </div>,
              getOverlayRootElement()
            );
          };

          return (
            <FeatureSurfacesDropdownPortal featureId={surfacesDropdownOpen} />
          );
        })()}

      {/* Risk Level Dropdown Portal */}
      {riskLevelDropdownOpen.featureId &&
        createPortal(
          <div
            ref={riskDropdownPopupRef}
            data-feature-risk-dropdown={riskLevelDropdownOpen.featureId}
            data-testid={`features-risk-dropdown-${riskLevelDropdownOpen.featureId}`}
            style={{
              position: 'fixed',
              top: `${riskLevelDropdownOpen.position.y}px`,
              left: `${riskLevelDropdownOpen.position.x}px`,
              zIndex: 1050,
              width: '235px',
              minWidth: '235px',
              willChange: 'transform'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className={styles.riskLevelDropdownMenu}
              data-testid="features-risk-dropdown-menu"
              role="menu"
            >
              <div
                className={styles.riskLevelMenuItem}
                data-testid="features-risk-item-low"
                tabIndex={0}
                role="menuitem"
                onMouseDown={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRiskLevelAction(
                    'low',
                    riskLevelDropdownOpen.featureId!
                  );
                }}
                onKeyDown={e => {
                  if (isActivationKey(e)) {
                    e.stopPropagation();
                    e.preventDefault();
                    handleRiskLevelAction(
                      'low',
                      riskLevelDropdownOpen.featureId!
                    );
                  }
                }}
              >
                <div className={styles.riskLevelMenuItemContent}>
                  <div className={styles.riskLevelMenuIcon}>
                    <img
                      src={RiskLowIcon}
                      alt="Low Risk"
                      width={16}
                      height={16}
                    />
                  </div>
                  <span>
                    Risk Level: <span style={{ fontWeight: 'bold' }}>Low</span>
                  </span>
                </div>
                {(() => {
                  const row = localFeatures.find(
                    r => r.id === riskLevelDropdownOpen.featureId
                  );
                  const v = String(row?.riskLevel || '').toLowerCase();
                  return v === 'low' || v === 'green';
                })() && <BlackCheckMarkIcon width={18} height={16} />}
              </div>
              <div
                className={styles.riskLevelMenuItem}
                data-testid="features-risk-item-moderate"
                tabIndex={0}
                role="menuitem"
                onMouseDown={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRiskLevelAction(
                    'moderate',
                    riskLevelDropdownOpen.featureId!
                  );
                }}
                onKeyDown={e => {
                  if (isActivationKey(e)) {
                    e.stopPropagation();
                    e.preventDefault();
                    handleRiskLevelAction(
                      'moderate',
                      riskLevelDropdownOpen.featureId!
                    );
                  }
                }}
              >
                <div className={styles.riskLevelMenuItemContent}>
                  <div className={styles.riskLevelMenuIcon}>
                    <img
                      src={RiskModerateIcon}
                      alt="Moderate Risk"
                      width={18}
                      height={18}
                    />
                  </div>
                  <span>
                    Risk Level:{' '}
                    <span style={{ fontWeight: 'bold' }}>Moderate</span>
                  </span>
                </div>
                {(() => {
                  const row = localFeatures.find(
                    r => r.id === riskLevelDropdownOpen.featureId
                  );
                  const v = String(row?.riskLevel || '').toLowerCase();
                  return v === 'moderate' || v === 'yellow';
                })() && <BlackCheckMarkIcon width={18} height={16} />}
              </div>
              <div
                className={styles.riskLevelMenuItem}
                data-testid="features-risk-item-high"
                tabIndex={0}
                role="menuitem"
                onMouseDown={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRiskLevelAction(
                    'high',
                    riskLevelDropdownOpen.featureId!
                  );
                }}
                onKeyDown={e => {
                  if (isActivationKey(e)) {
                    e.stopPropagation();
                    e.preventDefault();
                    handleRiskLevelAction(
                      'high',
                      riskLevelDropdownOpen.featureId!
                    );
                  }
                }}
              >
                <div className={styles.riskLevelMenuItemContent}>
                  <div className={styles.riskLevelMenuIcon}>
                    <img
                      src={RiskHighIcon}
                      alt="High Risk"
                      width={14}
                      height={14}
                    />
                  </div>
                  <span>
                    Risk Level: <span style={{ fontWeight: 'bold' }}>High</span>
                  </span>
                </div>
                {(() => {
                  const row = localFeatures.find(
                    r => r.id === riskLevelDropdownOpen.featureId
                  );
                  const v = String(row?.riskLevel || '').toLowerCase();
                  return v === 'high' || v === 'red';
                })() && <BlackCheckMarkIcon width={18} height={16} />}
              </div>
              <div
                className={styles.riskLevelMenuItem}
                data-testid="features-risk-item-planning"
                tabIndex={0}
                role="menuitem"
                onMouseDown={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleRiskLevelAction(
                    'in_planning',
                    riskLevelDropdownOpen.featureId!
                  );
                }}
                onKeyDown={e => {
                  if (isActivationKey(e)) {
                    e.stopPropagation();
                    e.preventDefault();
                    handleRiskLevelAction(
                      'in_planning',
                      riskLevelDropdownOpen.featureId!
                    );
                  }
                }}
              >
                <div className={styles.riskLevelMenuItemContent}>
                  <div className={styles.riskLevelMenuIcon}>
                    <img
                      src={RiskPlanningIcon}
                      alt="In Planning"
                      width={16}
                      height={16}
                    />
                  </div>
                  <span>In Planning</span>
                </div>
                {(() => {
                  const row = localFeatures.find(
                    r => r.id === riskLevelDropdownOpen.featureId
                  );
                  const v = String(row?.riskLevel || '').toLowerCase();
                  return v.includes('planning') || v === 'in_planning';
                })() && <BlackCheckMarkIcon width={18} height={16} />}
              </div>
            </div>
          </div>,
          getOverlayRootElement()
        )}

      {/* Options Dropdown */}
      {optionsMenu.featureId &&
        createPortal(
          <div
            ref={el => {
              optionsMenuRef.current = el;
              (optionsMenuPopupRef as any).current = el;
            }}
            className={styles.optionsMenu}
            data-testid="features-options-menu"
            style={{
              position: 'fixed',
              left: optionsMenu.position.x,
              top: optionsMenu.position.y,
              willChange: 'transform'
            }}
            role="menu"
            aria-label="Feature options"
          >
            {(() => {
              // Check if the feature is completed
              const currentFeature = localFeatures.find(
                f => f.id === optionsMenu.featureId
              );
              const isFeatureCompleted =
                String(currentFeature?.status || '').toUpperCase() ===
                STATUS_VALUES.COMPLETE;

              const isStatusChangeDisabled =
                !optionsMenu.featureId ||
                String(optionsMenu.featureId).startsWith('new-feature-');
              const statusChangeClass = `${styles.optionsItem} ${
                isStatusChangeDisabled ? styles.optionsItemDisabled : ''
              }`;

              // Show "Mark as Active" for completed features, "Mark as Complete" for others
              if (isFeatureCompleted) {
                return (
                  <div
                    className={statusChangeClass}
                    data-testid="features-options-active"
                    aria-disabled={isStatusChangeDisabled}
                    tabIndex={isStatusChangeDisabled ? -1 : 0}
                    role="menuitem"
                    onMouseEnter={e => {
                      // Remove focus on mouse enter so hover and focus don't conflict
                      (e.currentTarget as HTMLElement).blur();
                    }}
                    onMouseDown={e => {
                      if (isStatusChangeDisabled) return;
                      e.preventDefault();
                      e.stopPropagation();
                      if (optionsMenu.featureId)
                        handleMarkAsActive(optionsMenu.featureId);
                    }}
                    onKeyDown={e => {
                      if (isStatusChangeDisabled) return;
                      if (isActivationKey(e)) {
                        e.preventDefault();
                        e.stopPropagation();
                        if (optionsMenu.featureId)
                          handleMarkAsActive(optionsMenu.featureId);
                      }
                    }}
                  >
                    Mark as Active
                  </div>
                );
              } else {
                return (
                  <div
                    className={statusChangeClass}
                    data-testid="features-options-complete"
                    aria-disabled={isStatusChangeDisabled}
                    tabIndex={isStatusChangeDisabled ? -1 : 0}
                    role="menuitem"
                    onMouseEnter={e => {
                      // Remove focus on mouse enter so hover and focus don't conflict
                      (e.currentTarget as HTMLElement).blur();
                    }}
                    onMouseDown={e => {
                      if (isStatusChangeDisabled) return;
                      e.preventDefault();
                      e.stopPropagation();
                      if (optionsMenu.featureId)
                        handleMarkAsComplete(optionsMenu.featureId);
                    }}
                    onKeyDown={e => {
                      if (isStatusChangeDisabled) return;
                      if (isActivationKey(e)) {
                        e.preventDefault();
                        e.stopPropagation();
                        if (optionsMenu.featureId)
                          handleMarkAsComplete(optionsMenu.featureId);
                      }
                    }}
                  >
                    Mark as Complete
                  </div>
                );
              }
            })()}
            {(() => {
              const isSavedFeature =
                optionsMenu.featureId &&
                !String(optionsMenu.featureId).startsWith('new-feature-');
              if (!isSavedFeature) return null;
              return (
                <div
                  className={styles.optionsItem}
                  data-testid="features-options-clone"
                  tabIndex={0}
                  role="menuitem"
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).blur();
                  }}
                  onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (optionsMenu.featureId)
                      handleCloneFeature(optionsMenu.featureId);
                  }}
                  onKeyDown={e => {
                    if (isActivationKey(e)) {
                      e.preventDefault();
                      e.stopPropagation();
                      if (optionsMenu.featureId)
                        handleCloneFeature(optionsMenu.featureId);
                    }
                  }}
                >
                  Clone Feature
                </div>
              );
            })()}
            <div
              className={styles.optionsItem}
              data-testid="features-options-delete"
              tabIndex={0}
              role="menuitem"
              onMouseEnter={e => {
                // Remove focus on mouse enter so hover and focus don't conflict
                (e.currentTarget as HTMLElement).blur();
              }}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                if (optionsMenu.featureId)
                  handleConfirmDelete(optionsMenu.featureId);
              }}
              onKeyDown={e => {
                if (isActivationKey(e)) {
                  e.preventDefault();
                  e.stopPropagation();
                  if (optionsMenu.featureId)
                    handleConfirmDelete(optionsMenu.featureId);
                }
              }}
            >
              Delete Feature
            </div>
          </div>,
          getOverlayRootElement()
        )}
    </div>
  );
};

export default Features;
